import React, { useState, useEffect, useRef } from 'react';
import { useClickOutside } from '../utils/useClickOutside';
import { collection, onSnapshot, doc, addDoc, serverTimestamp, getDoc, setDoc, deleteDoc, getDocs, query } from '../firebase.config';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

import PurchaseBillTemplate from './BillTemplates/PurchaseBillTemplate';
import PurchaseBillNonGstTemplate from './BillTemplates/PurchaseBillNonGstTemplate';
import PurchaseOrderTemplate from './BillTemplates/PurchaseOrderTemplate';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import ActionButtons from './ActionButtons';
import imageManager from '../utils/imageManager';

import { convertImageToDataUrl, preloadBillImages, inlineImagesForPrint } from '../utils/imageUtils';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import CompactFilterBar from './CompactFilterBar';
import { roundTo2Decimals, calculateGSTAmount, calculateLineTotal } from '../utils/numberFormat';
import { formatDateToCompanyFormat } from '../utils/dateUtils';
import { getCollectionForCompany, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import useActiveCompanyProfile from '../hooks/useActiveCompanyProfile';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { 
  getPartyAdvance, 
  allocateAdvanceToBill, 
  markAdvanceUsed 
} from '../utils/advanceUtils';
import { 
  PreviewModal, 
  FormModal,
  StandardModal,
  StandardButton,
  useModalManager,
  MODAL_SECTIONS,
} from './Modal';
import ShareButton from './Reports/ShareButton';

// Custom ShareButton component for purchases (only PDF and Image)
const PurchaseShareButton = ({ onExportPDF, onExportImage, disabled = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  const handleShareClick = () => {
    console.log('PurchaseShareButton: Share button clicked');
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = async (action) => {
    console.log('PurchaseShareButton: Option clicked, action:', action);
    if (!action || isProcessing) return;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    let tempContainer;
    try {
      if (typeof action === 'function') {
        console.log('PurchaseShareButton: Executing action function');
        await action();
      }
    } catch (error) {
      console.error('PurchaseShareButton: Action execution failed:', error);
      // Silent error handling to prevent console spam
    } finally {
      setIsProcessing(false);
    }
  };

  const shareOptions = [
    { 
      label: '📄 Share as PDF', 
      action: onExportPDF,
      disabled: !onExportPDF
    },
    { 
      label: '🖼️ Share as Image', 
      action: onExportImage,
      disabled: !onExportImage
    }
  ].filter(option => !option.disabled);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleShareClick}
        disabled={disabled || isProcessing}
        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition duration-300 text-sm relative"
      >
        {isProcessing ? '⏳' : '📱'} Share
      </button>
      
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 sm:w-48 w-40"
          style={{ zIndex: 9999 }}
        >
          {shareOptions.length === 0 ? (
            <div className="p-2 text-gray-500 text-xs">No sharing options available</div>
          ) : (
            shareOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option.action)}
                disabled={option.disabled || isProcessing}
                className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Make imageManager globally available for PDF generation
if (typeof window !== 'undefined') {
  try {
    window.imageManager = imageManager;
    console.log('Purchases: imageManager assigned to window successfully');
  } catch (error) {
    console.error('Purchases: Failed to assign imageManager to window:', error);
    // Silent error handling
  }
}



const initialItemRow = {
  item: '',
  nos: 1,
  length: 0,
  height: 0,
  qty: 0,
  qtyExpr: '',
  qtyDisplay: '',
  lineDiscountType: 'amount',
  lineDiscountValue: 0,
  rate: 0,
  amount: 0,
  sgst: 0,
  cgst: 0,
  igst: 0,
  total: 0,
};



// Utility for rounding to 2 decimal places - now imported from numberFormat.js
// function round2(val) {
//   return Math.round((parseFloat(val) + Number.EPSILON) * 100) / 100;
// }

// Parse a quantity expression like "5x3x2" or "10*2" into a numeric product and a normalized display string
function parseQtyExpression(input) {
  const s = String(input || '').trim().replace(/\s+/g, '');
  if (!s) return { ok: false, value: 0, display: '' };
  const parts = s.split(/[xX\*]/).filter(Boolean);
  if (parts.length === 0) return { ok: false, value: 0, display: '' };
  let product = 1;
  for (const p of parts) {
    const n = Number(p);
    if (!isFinite(n) || n <= 0) return { ok: false, value: 0, display: '' };
    product *= n;
  }
  return { ok: true, value: product, display: parts.join('×') };
}

// Collapsible Purchase Card Component for Mobile View
const PurchaseCard = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany }) => {
    // Format date according to company settings (currentCompany prop is live profile from parent)
    const date = formatDateToCompanyFormat(bill.billDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    // Get bill items for this invoice - check both items and rows arrays
    const billItems = bill.items || bill.rows || [];

    // Helper function to get item name from item ID
    const getItemName = (itemId) => {
        if (!itemId || !items || !Array.isArray(items)) return null;
        const item = items.find(i => i.id === itemId || i.itemId === itemId);
        return item ? (item.name || item.itemName || item.description) : null;
    };

    return (
        <div 
            className="bg-white border border-gray-200 rounded-lg shadow-sm mb-0 transform transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02]"
            style={{
                animationName: 'slideInUp',
                animationDuration: '0.4s',
                animationTimingFunction: 'ease-out',
                animationFillMode: 'forwards',
                animationDelay: `${index * 50}ms`
            }}
        >
            {/* Header Section */}
            <div 
                className="px-3 py-2 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex justify-between items-center">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{bill.number || bill.invoiceNumber}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{bill.partyName || 'No Party'}</p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                        <div className="text-right">
                            <div className="text-sm sm:text-lg font-semibold text-blue-600">{amount}</div>
                            <div className="text-xs text-gray-500">{date}</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="px-1 sm:px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 mb-2">
                                {docTypeOptions.find(opt => opt.value === docType)?.label || 'Purchase Bill'}
                            </span>
                    </div>
                </div>
            </div>

                {/* Payment Status Row - Single Row Layout */}
                <div className="mt-2 sm:mt-3 flex justify-between items-center">
                    <div 
                        className="text-xs sm:text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-green-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                            onReceipts(bill);
                        }}
                        title="Click to view receipt details"
                    >
                        <span className="hidden sm:inline">Paid: </span>
                        <span className="sm:hidden">💰 </span>
                        {formatCurrency(bill.paid || 0, companyCurrency)}
                    </div>
                    <div 
                        className="text-xs sm:text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-red-50"
                                onClick={(e) => {
                                    e.stopPropagation();
                            onPayment(bill);
                        }}
                        title="Click to add payment"
                    >
                        <span className="hidden sm:inline">Outstanding: </span>
                        <span className="sm:hidden">💳 </span>
                        {formatCurrency(bill.outstanding || 0, companyCurrency)}
                    </div>
                </div>

                {/* Action Buttons with Dropdown */}
                <div className="mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 relative">
                    <div className="flex justify-between items-center">
                    <ActionButtons
                        enableDropdown={true}
                        billData={{
                            ...bill,
                            partyName: bill.partyName
                        }}
                        items={items}
                        actions={[
                            { type: 'edit', onClick: () => onEdit(bill) },
                            { type: 'delete', onClick: () => onDelete(bill.id) },
                            {
                                type: docType === 'purchaseBill' ? 'purchase' : 'purchaseOrder',
                                onClick: () => onPrint(bill)
                            }
                        ]}
                            className="justify-start"
                        size="md"
                    />
                            <button
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            </button>
                        </div>
            </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Item Name - Prominent on Mobile */}
                                        <div className="mb-1">
                                            <span className="text-sm sm:text-base font-medium text-gray-900 block" title={getItemName(item.item) || item.name || 'Unnamed Item'}>
                                                {getItemName(item.item) || item.name || 'Unnamed Item'}
                                            </span>
                                        </div>
                                        
                                        {/* Item Details - Responsive Layout */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                            <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                            <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                            <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Dropdown Form Component for Desktop Table Rows
const DropdownFormRow = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle }) => {
    // Format date as DD/MM/YYYY
    let date = bill.billDate || bill.date || "";
    if (date && date.includes("-")) {
        const [yyyy, mm, dd] = date.split("-");
        date = `${dd}/${mm}/${yyyy}`;
    }
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    const paidAmount = formatCurrency(bill.paid || 0, companyCurrency);
    const outstandingAmount = formatCurrency(bill.outstanding || 0, companyCurrency);

    // Get bill items for this bill - check both items and rows arrays
    const billItems = bill.items || bill.rows || [];

    // Helper function to get item name from item ID
    const getItemName = (itemId) => {
        if (!itemId || !items || !Array.isArray(items)) return null;
        const item = items.find(i => i.id === itemId || i.itemId === itemId);
        return item ? (item.name || item.itemName || item.description) : null;
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-2 transform transition-all duration-300 ease-in-out hover:shadow-md">
            {/* Main Row - Always Visible */}
            <div 
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-6 gap-2 lg:gap-4 items-center text-center">
                        <div className="text-sm font-medium text-gray-900 truncate" title={bill.number || bill.billNumber}>
                            {bill.number || bill.billNumber}
                        </div>
                        <div className="text-sm text-gray-600" title={date}>{date}</div>
                        <div className="text-sm text-gray-600 truncate" title={bill.partyName}>
                            {bill.partyName}
                        </div>
                        <div className="text-sm font-semibold text-blue-600" title={amount}>{amount}</div>
                        <div 
                            className="text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors hover:bg-green-50 px-1 py-0.5 rounded"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onReceipts(bill);
                                }}
                            title="Click to view receipt details"
                        >
                            {paidAmount}
                        </div>
                        <div 
                            className="text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors hover:bg-red-50 px-1 py-0.5 rounded"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onPayment(bill);
                            }}
                            title="Click to add payment"
                        >
                            {outstandingAmount}
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-2 lg:ml-4">
                        <ActionButtons
                            enableDropdown={true}
                            billData={{
                                ...bill,
                                partyName: bill.partyName
                            }}
                            items={items}
                            actions={[
                                { type: 'edit', onClick: () => onEdit(bill) },
                                { type: 'delete', onClick: () => onDelete(bill.id) },
                                {
                                    type: docType === 'purchaseBill' ? 'purchaseBill' : 'purchaseOrder',
                                    onClick: () => onPrint(bill)
                                }
                            ]}
                            size="sm"
                        />
                            <button
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? '-rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                            </button>
                        </div>
                </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-64 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        <div className="flex items-center justify-between text-sm">
                                            <div className="flex-1 min-w-0">
                                                <span className="font-medium text-gray-900 truncate" title={getItemName(item.item) || item.name || 'Unnamed Item'}>
                                                    {getItemName(item.item) || item.name || 'Unnamed Item'}
                                                </span>
                                            </div>
                                            <div className="flex items-center space-x-4 text-gray-600">
                                                <span className="text-xs">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                                <span className="text-xs">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                                <span className="text-xs font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                            </div>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal → Image Manager
  function PurchasesDev({ db, userId, isAuthReady, appId }) {
    const { currentCompany, companyProfile } = useActiveCompanyProfile();
    const companyId = currentCompany?.companyId || currentCompany?.id;
    const { currency: companyCurrency } = useCompanyCurrency();
    const { t } = useLanguage();
    // Modal manager hook
    const { } = useModalManager();
    
    // URL parameter handling for document type selection
    const location = useLocation();
    

  
  // Document type: purchaseBill or purchaseOrder
  const [docType, setDocType] = useState(() => {
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const tabParam = urlParams.get('tab');
    if (tabParam === 'orders') return 'purchaseOrder';
    return 'purchaseBill';
  });
  const docTypeOptions = [
    { value: 'purchaseBill', label: t('purchaseBill'), collection: 'purchaseBills', numberLabel: 'Purchase Bill Number' },
    { value: 'purchaseOrder', label: t('purchaseOrder'), collection: 'purchaseOrders', numberLabel: 'Purchase Order Number' },
  ];

  // Bill state
  const [billNumber, setBillNumber] = useState('');
  const [billDate, setBillDate] = useState(new Date().toISOString().split('T')[0]);
  const [party, setParty] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
  const [itemSearchTextByRow, setItemSearchTextByRow] = useState({});
  const [customFields, setCustomFields] = useState({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
  const [showEwayBill, setShowEwayBill] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEwayBillModal, setShowEwayBillModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDocTypeDropdown, setShowDocTypeDropdown] = useState(false);
  const [showGstInfoDropdown, setShowGstInfoDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocTypeDropdown && !event.target.closest('.relative')) {
        setShowDocTypeDropdown(false);
      }
      if (showGstInfoDropdown && !event.target.closest('.relative')) {
        setShowGstInfoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDocTypeDropdown, showGstInfoDropdown]);

  // Data
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [bills, setBills] = useState([]);
  const [company, setCompany] = useState({});
  
  // Ref to track bills for bill number generation without causing re-renders
  const billsRef = useRef([]);
  
  // Quick items functionality
  const [mostUsedItems, setMostUsedItems] = useState([]);
  const [partyWiseItems, setPartyWiseItems] = useState([]);
  const [selectedPartyForQuickItems, setSelectedPartyForQuickItems] = useState('');
  const [showQuickItemsModal, setShowQuickItemsModal] = useState(false);
  const [quickItemModalPosition, setQuickItemModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  const quickItemButtonRef = useRef(null);
  
  // Mobile edit modal states
  const [showMobileEditModal, setShowMobileEditModal] = useState(false);
  const [partySearchText, setPartySearchText] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  
  // Filtered parties for mobile search
  const filteredParties = parties.filter(p => 
    p.firmName.toLowerCase().includes(partySearchText.toLowerCase())
  );
  
  // Close party dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPartyDropdown && !event.target.closest('.party-dropdown-container')) {
        setShowPartyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPartyDropdown]);
  
  // Mobile drawer functionality
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Dropdown form state for expanded rows
  const [expandedRows, setExpandedRows] = useState({});

  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Filter states
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('purchases_showFilters');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [filterSearch, setFilterSearch] = useState(''); // Combined search for bill number and party name
  const [filterDate, setFilterDate] = useState('');
  const [filterAmount, setFilterAmount] = useState('');

  // UI
    const [editingBillId, setEditingBillId] = useState(null);
  const [message, setMessage] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceBill, setInvoiceBill] = useState(null);
  const [invoiceZoom, setInvoiceZoom] = useState(1);
  const invoiceRef = useRef();
  const tableRef = useRef();
  const [viewBill, setViewBill] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedBillId, setSavedBillId] = useState(null);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // ADD ITEM Modal states
  const [newItemName, setNewItemName] = useState('');
  const [newItemMeasurement, setNewItemMeasurement] = useState('');
  const [newItemRate, setNewItemRate] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemHsnCode, setNewItemHsnCode] = useState('');
  const [newItemGstPercentage, setNewItemGstPercentage] = useState('0');
  const [newItemPurchasePrice, setNewItemPurchasePrice] = useState('0');
  const [newItemSalePrice, setNewItemSalePrice] = useState('0');
  const [newItemCurrentStock, setNewItemCurrentStock] = useState('0');
  const [newItemIsActive, setNewItemIsActive] = useState(true);
  const [newItemCustomUnit, setNewItemCustomUnit] = useState('');
  const [addItemMessage, setAddItemMessage] = useState('');
  
  // ADD PARTY Modal states
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyGstin, setNewPartyGstin] = useState('');
  const [newPartyAddress, setNewPartyAddress] = useState('');
  const [newPartyPhone, setNewPartyPhone] = useState('');
  const [newPartyEmail, setNewPartyEmail] = useState('');
  const [newPartyGstType, setNewPartyGstType] = useState('Unregistered');
  const [addPartyMessage, setAddPartyMessage] = useState('');
  
  // Additional party fields
  const [newPartyOpeningBalance, setNewPartyOpeningBalance] = useState('');
  const [newPartyCreditLimit, setNewPartyCreditLimit] = useState('');
  const [newPartyCreditPeriod, setNewPartyCreditPeriod] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  // Mobile view carousel state
  const [currentMobileItemIndex, setCurrentMobileItemIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Keep docType and edit/view ids in URL for deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const desiredTab = docType === 'purchaseOrder' ? 'orders' : 'bills';
    if (params.get('tab') !== desiredTab) params.set('tab', desiredTab);
    if (editingBillId) params.set('edit', editingBillId); else params.delete('edit');
    const base = window.location.hash.split('?')[0] || '#/purchases';
    const next = `${base}?${params.toString()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [docType, editingBillId]);

  // On first load, if view/edit present open it
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const viewId = params.get('view');
    const editId = params.get('edit');
    if (viewId) {
      const collectionName = docType === 'purchaseOrder' ? 'purchaseOrders' : 'purchaseBills';
      getDoc(doc(db, 'companies', companyId, collectionName, viewId)).then(snap => {
        if (snap.exists()) { setViewBill({ id: snap.id, ...snap.data() }); setShowViewModal(true); }
      }).catch(() => {});
    }
    if (editId) setEditingBillId(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payment Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [payments, setPayments] = useState([]);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState(null);

  // Payment Receipt Preview states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
  const receiptRef = useRef();
  
  // Payment Details Modal states (for multiple receipts)
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedBillForReceipts, setSelectedBillForReceipts] = useState(null);
  
  // Click outside refs for modals
  const successModalRef = useClickOutside(() => setShowSuccessModal(false), showSuccessModal);
  const paymentModalRef = useClickOutside(() => setShowPaymentModal(false), showPaymentModal);
  const addItemModalRef = useClickOutside(() => setShowAddItemModal(false), showAddItemModal);

  // Table sorting hook with default sort by bill number descending (LIFO)
  const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'billNumber', direction: 'desc' });
  

  


  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Add Party Modal → Add Item Modal → Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showAddPartyModal) {
          setShowAddPartyModal(false);
          clearAddPartyForm();
        } else if (showAddItemModal) {
          setShowAddItemModal(false);
        } else if (showReceiptModal) {
          setShowReceiptModal(false);
        } else if (showPaymentDetailsModal) {
          setShowPaymentDetailsModal(false);
        } else if (showPaymentModal) {
          setShowPaymentModal(false);
        } else if (showInvoiceModal) {
          setShowInvoiceModal(false);
        } else if (showViewModal) {
          setShowViewModal(false);
        } else if (showSuccessModal) {
          setShowSuccessModal(false);
        }
      }
    };
    
    // Add event listener if any modal is open
    if (showAddPartyModal || showAddItemModal || showReceiptModal || showPaymentDetailsModal || showPaymentModal || showInvoiceModal || showViewModal || showSuccessModal) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showAddPartyModal, showAddItemModal, showReceiptModal, showPaymentDetailsModal, showPaymentModal, showInvoiceModal, showViewModal, showSuccessModal]);

  // Define handleViewBill
  const handleViewBill = (bill) => {
    setViewBill(bill);
    setShowViewModal(true);
  };

  // Fetch parties (sellers)
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
      // Path updated to use getCollectionForCompany
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const unsubscribe = onSnapshot(
        partiesCollectionRef,
        (snapshot) => {
          const allParties = [];
                snapshot.forEach((doc) => {
            allParties.push({ id: doc.id, ...doc.data() });
          });
          allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
          setParties(allParties);
        }, (error) => {
          console.error('Error fetching parties:', error);
          setParties([]);
        }
      );
      return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch items
    useEffect(() => {
        if (db && userId && isAuthReady && companyId) {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      const unsubscribe = onSnapshot(itemsCollectionRef, (snapshot) => {
        const itemsArr = [];
                snapshot.forEach((doc) => {
          itemsArr.push({ id: doc.id, ...doc.data() });
        });
        itemsArr.sort((a, b) => (a.itemName || '').localeCompare(b.itemName || ''));
        setItems(itemsArr);
      }, (error) => {
        console.error('Error fetching items:', error);
        setItems([]);
      });
            return () => unsubscribe();
        }
    }, [db, userId, isAuthReady, appId, companyId]);

  // Fetch company details
  useEffect(() => {
    if (db && isAuthReady && companyId) {
      const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
        if (docSnap.exists()) {
          setCompany(normalizeCompanyDocSnapshot(docSnap.data() || {}, companyId));
        }
      }, (error) => {
        console.error('Error fetching company details:', error);
        setCompany({});
      });
      return () => unsubscribe();
    }
  }, [db, isAuthReady, companyId]);

  // Fetch payments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
        const paymentsData = [];
        snapshot.forEach((doc) => {
          paymentsData.push({ id: doc.id, ...doc.data() });
        });
        setPayments(paymentsData);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);
  


  // Generate receipt number for purchase payments
  const generateReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = 'P'; // Purchase payments
    
    try {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      let highestSequence = 0;
      
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {
          const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
          const match = payment.receiptNumber.match(regexPattern);
          
          if (match) {
            const sequence = parseInt(match[1]);
            if (sequence > highestSequence) {
              highestSequence = sequence;
            }
          }
        }
      });
      
      const nextSequence = highestSequence + 1;
      return `PR${paymentType}${financialYear}/${nextSequence}`;
    } catch (error) {
      console.error('Error generating receipt number:', error);
      return `PR${paymentType}${financialYear}/1`;
    }
  };

  // Get party name by ID
  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Get party GST type by ID
  const getPartyGstType = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.gstType || 'Unregistered') : 'Unregistered';
  };

  // Calculate outstanding amount for a bill
  const getBillOutstanding = (bill) => {
    const billPayments = payments.filter(p => 
      p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'purchase')
    );
    
    const totalPaid = billPayments.reduce((sum, payment) => {
      const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === 'purchase');
      return sum + (allocation ? allocation.allocatedAmount : 0);
    }, 0);
    
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    return totalAmount - totalPaid;
  };

  // Filter functions
  const clearFilters = () => {
    setFilterSearch('');
    setFilterDate('');
    setFilterAmount('');
  };

  // Handle filter changes from CompactFilterBar
  const handleFilterChange = (filters) => {
    setFilterSearch(filters.search || '');
    setFilterDate(filters.date || '');
    setFilterAmount(filters.amount || '');
  };

  const getFilteredBills = (billsList) => {
    return billsList.filter(bill => {
      // Combined search for bill number and party name
      if (filterSearch) {
        const searchTerms = filterSearch.toLowerCase().trim().split(/\s+/);
        const billNumber = (bill.number || '').toLowerCase();
        const partyName = getPartyName(bill.party).toLowerCase();
        
        // Check if all search terms match either bill number or party name
        if (!searchTerms.every(term => 
          billNumber.includes(term) || partyName.includes(term)
        )) {
          return false;
        }
      }
      
      // Filter by date
      if (filterDate) {
        const billDate = bill.billDate || bill.invoiceDate || '';
        if (!billDate.toLowerCase().includes(filterDate.toLowerCase())) {
          return false;
        }
      }
      
      // Filter by amount
      if (filterAmount) {
        const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
        const amountStr = totalAmount.toString();
        if (!amountStr.includes(filterAmount)) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Table pagination hook - placed after function definitions
  const filteredBills = getFilteredBills(bills);
  const sortedBills = getSortedData(filteredBills.map((bill) => {
    const partyName = getPartyName(bill.party);
    const outstanding = getBillOutstanding(bill);
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    const paid = totalAmount - outstanding;
    return {
      ...bill,
      partyName,
      paid: Math.max(0, paid),
      outstanding: Math.max(0, outstanding)
    };
  }));
  const pagination = useTablePagination(sortedBills, 10);

  // Fetch most used items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && bills.length > 0) {
      // Aggregate item usage from purchase bills
      const itemUsage = {};
      
      bills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = bill.items || bill.rows || [];
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.billDate || bill.date ? new Date(bill.billDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 most used items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setMostUsedItems(itemsWithNames);
    } else {
      setMostUsedItems([]);
    }
  }, [db, userId, isAuthReady, bills, items]);

  // Fetch party-wise items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && selectedPartyForQuickItems && bills.length > 0) {
      // Filter bills by selected party
      const partyBills = bills.filter(bill => bill.party === selectedPartyForQuickItems);
      
      // Aggregate item usage from party-specific bills
      const itemUsage = {};
      
      partyBills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = bill.items || bill.rows || [];
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.billDate || bill.date ? new Date(bill.billDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 party-specific items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setPartyWiseItems(itemsWithNames);
    } else {
      setPartyWiseItems([]);
    }
  }, [db, userId, isAuthReady, selectedPartyForQuickItems, items]);

  // Update party search text when party changes
  useEffect(() => {
    if (party) {
      const selectedParty = parties.find(p => p.id === party);
      if (selectedParty) {
        setPartySearchText(selectedParty.firmName);
      }
    } else {
      setPartySearchText('');
    }
  }, [party, parties]);

  // Fetch bills for the selected type with auto-refresh
  // This useEffect automatically refreshes data when bills are saved, edited, or deleted
  // No manual refresh button needed - data updates in real-time
  useEffect(() => {
    if (!db || !userId || !isAuthReady || !appId) return;
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    if (!companyId) return;
    
    try {
      const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, docType === 'purchaseBill' ? 'purchaseBills' : 'purchaseOrders');
      const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ id: doc.id, ...doc.data() });
        });
        // LIFO sorting is now handled by the pagination utility
        setBills(arr);
        billsRef.current = arr; // Update ref to avoid infinite loops
      }, (error) => {
        console.error('Error fetching bills:', error);
        setBills([]);
      });
      return () => unsubscribe();
    } catch (err) {
      console.error('Firestore listener setup failed:', err);
      setBills([]);
    }
  }, [db, userId, isAuthReady, appId, companyId, docType]);

  // Save showFilters state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('purchases_showFilters', JSON.stringify(showFilters));
    }
  }, [showFilters]);

  // Auto-generate number for the selected type
  useEffect(() => {
    if (editingBillId) return;
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const prefixMap = { purchaseBill: 'PRB', purchaseOrder: 'PRO' };
    const prefix = prefixMap[docType] || 'PRB';
    const fy = getFinancialYear(billDate);
    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
    
    // Use bills state to generate number - this will trigger when bills are loaded
    const serials = bills
      .filter(bill => (bill.number || '').startsWith(prefix + fyShort))
      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
      .filter(n => !isNaN(n));
    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
    setBillNumber(`${prefix}${fyShort}/${nextSerial}`);
  }, [billDate, docType, editingBillId, bills]);

  // Helper
  function getFinancialYear(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    if (month >= 4) {
      return `${year}-${(year + 1).toString().slice(-2)}`;
    } else {
      return `${year - 1}-${year.toString().slice(-2)}`;
    }
  }

  // Calculate modal position based on button click
  const calculateModalPosition = (buttonElement) => {
    if (!buttonElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const modalHeight = Math.min(viewportHeight * 0.85, 600); // Estimated modal height
    const modalWidth = Math.min(viewportWidth - 32, 672); // max-w-2xl = 672px, with 16px margin on each side
    
    let top = rect.bottom + 8; // 8px below the button
    let left = rect.left;
    
    // Adjust if modal would go below viewport
    if (top + modalHeight > viewportHeight - 16) {
      top = Math.max(16, rect.top - modalHeight - 8); // 8px above button, min 16px from top
    }
    
    // Adjust if modal would go beyond right edge
    if (left + modalWidth > viewportWidth - 16) {
      left = viewportWidth - modalWidth - 16;
    }
    
    // Adjust if modal would go beyond left edge
    if (left < 16) {
      left = 16;
    }
    
    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    };
  };

  // Quick items helper functions
  const handleQuickItemClick = (itemData, isPartySpecific = false) => {
    // Use itemId consistently - check both itemId and id fields
    const targetItemId = itemData.itemId || itemData.id;
    if (!targetItemId) {
      return;
    }
    
    // Find the item details from items array
    const itemDetails = items.find(item => item.id === targetItemId);
    if (!itemDetails) {
      return;
    }
    
    // Find an empty row or add a new one
    let targetRowIndex = rows.findIndex(row => !row.item && !row.itemId);
    if (targetRowIndex === -1) {
      // Add new row if all rows are filled
      setRows(prev => [...prev, { ...initialItemRow }]);
      targetRowIndex = rows.length;
    }
    
    // Calculate the amount
    const rate = itemData.lastRate || itemDetails.purchasePrice || itemDetails.rate || 0;
    const qty = itemData.lastQty || 1;
    const amount = rate * qty;
    
    // Update the row with item details using handleRowChange to ensure proper calculations
    // First set the item to trigger GST calculations
    handleRowChange(targetRowIndex, 'item', targetItemId);
    
    // Then set the rate and quantity in sequence to ensure proper calculations
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'rate', rate.toString());
    }, 50);
    
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'qty', qty.toString());
    }, 100);
    
    // Update item search text for this row
    setItemSearchTextByRow(prev => ({
      ...prev,
      [targetRowIndex]: itemDetails.itemName || itemDetails.name || 'Unknown Item'
    }));
  };

  const getResponsiveColumns = () => {
    if (typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1920) return 3; // Large desktop
      if (screenWidth >= 1366) return 2; // Medium desktop
      return 1; // Small desktop/mobile
    }
    return 1; // Default fallback
  };

  // Mobile helper functions
  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const isMobileView = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  };

  // Mobile-specific invoice modal handler
  const handleMobileInvoiceView = (bill) => {
    setInvoiceBill(bill);
    setShowInvoiceModal(true);
    
    // Ensure mobile viewport is properly set
    if (isMobileView()) {
      // Force viewport positioning for mobile
      setTimeout(() => {
        const modal = document.querySelector('[class*="fixed"][class*="inset-0"][class*="bg-black"][class*="bg-opacity-50"]');
        if (modal) {
          modal.style.position = 'fixed';
          modal.style.top = '0';
          modal.style.left = '0';
          modal.style.right = '0';
          modal.style.bottom = '0';
          modal.style.width = '100vw';
          modal.style.height = '100vh';
          modal.style.zIndex = '9999';
          modal.style.display = 'flex';
          modal.style.alignItems = 'stretch';
          modal.style.justifyContent = 'center';
          modal.style.margin = '0';
          modal.style.padding = '0';
          modal.style.transform = 'none';
        }
      }, 100);
    }
  };

  // Add/Edit/Delete item rows
  const handleRowChange = (idx, field, value) => {
    setRows(currentRows => {
      const updatedRows = currentRows.map((row, i) => {
        if (i !== idx) return row;
        let updated = { ...row, [field]: value };

        // Always keep gstPercent from item
        if (field === 'item') {
          let itemObj = items.find(it => it.id === value);
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
          if (!updated.nos) updated.nos = 1;
          // Prefer last party-wise rate from purchase bills; fallback to item.purchasePrice
          const partyId = party;
          let defaultRate = 0;
          try {
            for (let bIndex = bills.length - 1; bIndex >= 0; bIndex -= 1) {
              const bill = bills[bIndex];
              if ((bill.partyId || bill.party) !== partyId) continue;
              const foundRow = (bill.rows || []).find(r => r.item === value);
              if (foundRow && foundRow.rate) { defaultRate = parseFloat(foundRow.rate) || 0; break; }
            }
          } catch (e) { /* no-op */ }
          if (!defaultRate && itemObj) {
            defaultRate = parseFloat(itemObj.purchasePrice || itemObj.rate || 0) || 0;
          }
          if (!updated.rate || updated.rate === 0) {
            updated.rate = defaultRate;
          }
        }
        if (updated.gstPercent === undefined) {
          let itemObj = items.find(it => it.id === (field === 'item' ? value : row.item));
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
        }

        // Get GSTINs
        const sellerGstin = company.gstin || '';
        const partyObj = parties.find(p => p.id === (field === 'party' ? value : party));
        const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
        const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';

        // Always auto-calculate GST split if item or party is changed
        if (field === 'item' || field === 'party') {
          // Only calculate GST if party is Regular
          if (partyGstType === 'Regular') {
            if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
              if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
                // Same state: split GST
                updated.sgst = updated.gstPercent / 2;
                updated.cgst = updated.gstPercent / 2;
                updated.igst = 0;
              } else {
                // Different state: IGST only
                updated.sgst = 0;
                updated.cgst = 0;
                updated.igst = updated.gstPercent;
              }
            } else {
              // Fallback: split GST
              updated.sgst = updated.gstPercent / 2;
              updated.cgst = updated.gstPercent / 2;
              updated.igst = 0;
            }
          } else {
            // For Composition/Unregistered parties, set GST to 0
            updated.sgst = 0;
            updated.cgst = 0;
            updated.igst = 0;
          }
        }

        // Quantity calculation: prefer qty expression; fallback to Nos×Length×Height
        let itemObj = items.find(it => it.id === (field === 'item' ? value : row.item));
        const unit = itemObj ? itemObj.quantityMeasurement : '';
        
        // Set unit and quantityMeasurement in the row whenever item is present
        if (itemObj && (field === 'item' || !updated.unit)) {
          updated.unit = unit;
          updated.quantityMeasurement = unit;
        }
        
        const parsed = parseQtyExpression(updated.qtyExpr);
        let computedQty;
        if (parsed.ok) {
          computedQty = parsed.value;
          updated.qtyDisplay = parsed.display;
        } else {
          computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
          if (!updated.qtyExpr) {
            const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
            updated.qtyDisplay = `${a}×${b}×${c}`;
          }
        }
        updated.qty = computedQty;
        // Keep nos in sync for legacy logic
        updated.nos = computedQty;
        // Apply per-line discount to amount
        const rawAmount = calculateLineTotal(updated.qty, updated.rate);
        let lineDiscAmt = 0;
        if ((updated.lineDiscountType || 'amount') === 'percent') {
          lineDiscAmt = calculateGSTAmount(rawAmount, updated.lineDiscountValue);
        } else {
          lineDiscAmt = roundTo2Decimals(parseFloat(updated.lineDiscountValue) || 0);
        }
        lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
        updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);
        
        // GST and total calculation - only for Regular parties
        const currentPartyForGst = parties.find(p => p.id === party);
        const currentPartyGstType = currentPartyForGst ? (currentPartyForGst.gstType || 'Unregistered') : 'Unregistered';
        
        if (currentPartyGstType === 'Regular') {
          const sgstAmt = calculateGSTAmount(updated.amount, updated.sgst);
          const cgstAmt = calculateGSTAmount(updated.amount, updated.cgst);
          const igstAmt = calculateGSTAmount(updated.amount, updated.igst);
          updated.total = roundTo2Decimals((parseFloat(updated.amount) || 0) + sgstAmt + cgstAmt + igstAmt);
        } else {
          // For Composition/Unregistered parties, total equals amount (no GST)
          updated.total = roundTo2Decimals(parseFloat(updated.amount) || 0);
        }
        return updated;
      });

      // Auto-add trailing row logic
      const isRowNonEmpty = (r) => !!(r.item || (parseFloat(r.rate) || 0) || (r.qtyExpr && String(r.qtyExpr).trim()) || (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.lineDiscountValue) || 0));
      const last = updatedRows[updatedRows.length - 1];
      if (isRowNonEmpty(last)) {
        updatedRows.push({ ...initialItemRow });
      }
      while (updatedRows.length < 5) updatedRows.push({ ...initialItemRow });
      return updatedRows;
    });
  };
  const addRow = () => {
    setRows([...rows, { ...initialItemRow }]);
    // Move to the new item in mobile view
    setCurrentMobileItemIndex(rows.length);
  };
  const removeRow = idx => {
    setRows(rows.filter((_, i) => i !== idx));
    // Adjust mobile carousel index
    if (currentMobileItemIndex >= rows.length - 1) {
      setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
    }
  };

  // Mobile carousel navigation functions
  const goToPreviousItem = () => {
    setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
  };

  const goToNextItem = () => {
    setCurrentMobileItemIndex(Math.min(rows.length - 1, currentMobileItemIndex + 1));
  };

  const goToItem = (index) => {
    setCurrentMobileItemIndex(index);
  };

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentMobileItemIndex < rows.length - 1) {
      goToNextItem();
    }
    if (isRightSwipe && currentMobileItemIndex > 0) {
      goToPreviousItem();
    }
  };

  // ADD PARTY Modal functions
  const clearAddPartyForm = () => {
    setNewPartyName('');
    setNewPartyGstin('');
    setNewPartyAddress('');
    setNewPartyPhone('');
    setNewPartyEmail('');
    setNewPartyGstType('Unregistered');
    setNewPartyOpeningBalance('');
    setNewPartyCreditLimit('');
    setNewPartyCreditPeriod('');
    setShowAdvancedFields(false);
    setAddPartyMessage('');
  };

  const handleAddParty = async () => {
    if (!db || !userId) {
      setAddPartyMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    if (!newPartyName.trim()) {
      setAddPartyMessage("Party name is required.");
      return;
    }

    // Validate GSTIN for Regular parties
    if (newPartyGstType === 'Regular' && !newPartyGstin.trim()) {
      setAddPartyMessage("GSTIN is mandatory for Regular parties.");
      return;
    }

    try {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const partyData = {
        firmName: newPartyName.trim(),
        personName: '',
        address: newPartyAddress.trim(),
        contact: newPartyPhone.trim(),
        email: newPartyEmail.trim(),
        whatsapp: newPartyPhone.trim(),
        gstin: newPartyGstin.trim(),
        gstType: newPartyGstType,
        pan: '',
        city: '',
        stateName: '',
        pincode: '',
        creditPeriod: parseFloat(newPartyCreditPeriod) || 0,
        creditLimit: parseFloat(newPartyCreditLimit) || 0,
        openingBalance: parseFloat(newPartyOpeningBalance) || 0,
        partyType: 'Seller',
        useManualBalance: true,
        manualBalance: 0,
        manualBalanceDate: new Date().toISOString().split('T')[0],
        manualBalanceRemarks: '',
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      const docRef = await addDoc(partiesCollectionRef, partyData);
      setAddPartyMessage("Party added successfully!");
      
      // Auto-select the newly added party
      setParty(docRef.id);
      setSelectedPartyForQuickItems(docRef.id);
      setPartySearchText(newPartyName.trim());
      
      clearAddPartyForm();
      setShowAddPartyModal(false);
    } catch (error) {
      console.error("Error adding party:", error);
      setAddPartyMessage("Error adding party. Please try again.");
    }
  };
  
  // ADD ITEM Modal functions
  const clearAddItemForm = () => {
    setNewItemName('');
    setNewItemMeasurement('');
    setNewItemRate('');
    setNewItemDescription('');
    setNewItemHsnCode('');
    setNewItemGstPercentage('0');
    setNewItemPurchasePrice('0');
    setNewItemSalePrice('0');
    setNewItemCurrentStock('0');
    setNewItemIsActive(true);
    setNewItemCustomUnit('');
    setAddItemMessage('');
  };

  const handleAddItem = async () => {
    if (!db || !userId) {
      setAddItemMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    if (!newItemName.trim()) {
      setAddItemMessage("Please enter item name.");
      return;
    }

    try {
      const itemsCollectionRef = collection(db, 'companies', companyId, 'items');
      const itemData = {
        itemName: newItemName.trim(),
        quantityMeasurement: newItemMeasurement.trim() || 'Nos',
        rate: parseFloat(newItemRate) || 0,
        description: newItemDescription.trim(),
        hsn: newItemHsnCode.trim(),
        gstPercentage: parseFloat(newItemGstPercentage) || 0,
        purchasePrice: parseFloat(newItemPurchasePrice) || 0,
        salePrice: parseFloat(newItemSalePrice) || 0,
        currentStock: parseFloat(newItemCurrentStock) || 0,
        isActive: newItemIsActive,
        customUnit: newItemCustomUnit.trim(),
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      await addDoc(itemsCollectionRef, itemData);
      setAddItemMessage("Item added successfully!");
      clearAddItemForm();
      setShowAddItemModal(false);
    } catch (error) {
      console.error("Error adding item:", error);
      setAddItemMessage("Error adding item. Please try again.");
    }
  };



  // Save/Update bill
  const handleSaveBill = async () => {
    if (!db || !userId || !appId) return;
    
    // Filter out empty rows (rows without items)
    const filledRows = rows.filter(row => row.item && row.item.trim() !== '');
    
    if (!party || filledRows.length === 0) {
      alert("Please select a party and add at least one item.");
            return;
        }
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
  const subtotal = roundTo2Decimals(filledRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
  
  // Get party GST type to determine GST calculation
  const partyObj = parties.find(p => p.id === party);
  const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
  
  let totalSGST = 0, totalCGST = 0, totalIGST = 0, grandTotal = 0;
  
  if (partyGstType === 'Regular') {
    // Calculate GST only for Regular parties
    totalSGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.sgst), 0);
    totalCGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.cgst), 0);
    totalIGST = filledRows.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.igst), 0);
    grandTotal = roundTo2Decimals(subtotal + totalSGST + totalCGST + totalIGST);
  } else {
    // For Composition/Unregistered parties, no GST calculation
    totalSGST = 0;
    totalCGST = 0;
    totalIGST = 0;
    grandTotal = roundTo2Decimals(subtotal); // Total equals subtotal (no GST added)
  }
    const billData = {
      number: billNumber,
      billDate,
      party,
      notes,
      rows: filledRows,
      customFields,
      amount: grandTotal,
      createdAt: serverTimestamp(),
    };

    const allBillCollections = ['salesBills', 'challans', 'quotations', 'purchaseBills', 'purchaseOrders'];
    let allBills = [];
    for (const coll of allBillCollections) {
      const billsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, coll));
      billsSnap.forEach(doc => allBills.push({ ...doc.data(), id: doc.id, collection: coll }));
    }
    // 1. Check for duplicate number
    if (allBills.some(b => b.number === billNumber && (editingBillId ? b.id !== editingBillId : true))) {
      setErrorMessage('Bill number already exists in another document. Please use a unique number.');
      alert('Bill number already exists in another document. Please use a unique number.');
      return;
    }
    // 2. Gap/date check: allow if any bill in FY has the same date
    const fy = getFinancialYear(billDate);
    const fyBills = allBills.filter(b => (b.number || '').startsWith(fy));
    const thisSerial = parseInt((billNumber || '').split('/')[1], 10);
    const thisDate = new Date(billDate);
    const sameDateExists = fyBills.some(b => {
      if (!b.number || (!b.invoiceDate && !b.billDate)) return false;
      if (b.id === editingBillId) return false;
      const date = new Date(b.invoiceDate || b.billDate);
      return date.getTime() === thisDate.getTime();
    });
    if (!sameDateExists) {
      // Find previous and next serials
      const sortedBills = fyBills
        .filter(b => b.id !== editingBillId && b.number)
        .map(b => ({
          serial: parseInt((b.number || '').split('/')[1], 10),
          date: new Date(b.invoiceDate || b.billDate),
        }))
        .sort((a, b) => a.serial - b.serial);
      let prev = null, next = null;
      for (let i = 0; i < sortedBills.length; i++) {
        if (sortedBills[i].serial < thisSerial) prev = sortedBills[i];
        if (sortedBills[i].serial > thisSerial) { next = sortedBills[i]; break; }
      }
      if (prev && thisDate.getTime() < prev.date.getTime()) {
        setErrorMessage(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`);
        alert(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`);
        return;
      }
      if (next && thisDate.getTime() > next.date.getTime()) {
        setErrorMessage(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`);
        alert(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`);
        return;
      }
    }

    try {
      let savedBill;
      if (editingBillId) {
        const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), editingBillId);
        await setDoc(billRef, billData, { merge: true });
        savedBill = { id: editingBillId, ...billData };
      } else {
        const docRef = await addDoc(collectionRef, billData);
        savedBill = { id: docRef.id, ...billData };
      }

      // --- ADVANCE ALLOCATION LOGIC ---
      // Only allocate advance for purchase bills (not purchase orders)
      if (docType === 'purchaseBill') {
        const availableAdvance = getPartyAdvance(party, payments);
        if (availableAdvance > 0) {
          const { allocatedAdvance, advanceAllocations, remainingBillAmount } = allocateAdvanceToBill(party, grandTotal, payments);
          if (allocatedAdvance > 0) {
            // Mark advances as used
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments);
            // Optionally, create a payment record for advance allocation
            const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
            await addDoc(paymentsRef, {
              receiptNumber: `ADV-${billNumber}`,
              paymentDate: new Date().toISOString().split('T')[0],
              partyId: party,
              partyName: parties.find(p => p.id === party)?.firmName || '',
              totalAmount: allocatedAdvance,
              paymentMode: 'Advance Allocation',
              reference: 'Auto-advance',
              notes: 'Advance automatically allocated to new purchase bill',
              type: 'purchase',
              paymentType: 'advance-allocation',
              billId: savedBill.id,
              billNumber: billNumber,
              allocations: [{
                billType: 'purchase',
                billId: savedBill.id,
                billNumber: billNumber,
                allocatedAmount: allocatedAdvance,
                billOutstanding: grandTotal,
                isFullPayment: allocatedAdvance >= grandTotal
              }],
              advanceAllocations,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      setBillNumber('');
      setBillDate(new Date().toISOString().split('T')[0]);
      setParty('');
      setNotes('');
      setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
      setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
      setCurrentMobileItemIndex(0);
      setEditingBillId(null);
      setSavedBillId(savedBill.id);
      setShowSuccessModal(true);
    } catch (err) {
      alert("Error saving bill: " + err.message);
    }
  };

  // Function to clear bill form fields
  const clearBillForm = () => {
    setBillNumber('');
    setBillDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setNotes('');
    setRows([{ ...initialItemRow }]);
    setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
    setEditingBillId(null);
    setMessage('');
    setItemSearchTextByRow({});
    setCurrentMobileItemIndex(0);
  };

  // Edit bill
  const handleEditBill = (bill) => {
    setEditingBillId(bill.id);
    setBillNumber(bill.number || '');
    setBillDate(bill.billDate || '');
    setParty(bill.party || '');
    setNotes(bill.notes || '');
    setRows(bill.rows || [{ ...initialItemRow }]);
    setCustomFields(bill.customFields || { ewayBillNo: '', ewayQr: '', ewayDate: '' });
    
    // Initialize itemSearchTextByRow for mobile view
    const newItemSearchText = {};
    if (bill.rows && bill.rows.length > 0) {
      bill.rows.forEach((row, index) => {
        if (row.item) {
          const item = items.find(it => it.id === row.item);
          if (item) {
            newItemSearchText[index] = item.itemName || item.name || '';
          }
        }
      });
    }
    setItemSearchTextByRow(newItemSearchText);
    
    // Reset mobile carousel to first item
    setCurrentMobileItemIndex(0);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Delete bill
  const handleDeleteBill = async (billId) => {
    if (!db || !userId || !appId) return;
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), billId);
    await deleteDoc(billRef);
    setMessage('Bill deleted successfully!');
  };

  // Payment handling functions
  // Get bill payments for receipt preview
  const getBillPayments = (bill) => {
    if (!bill || !bill.party) {
      return [];
    }
    
    return payments.filter(payment => {
      // Check if payment is for the same party
      const paymentPartyId = payment.partyId || payment.party;
      if (paymentPartyId !== bill.party) {
        return false;
      }
      
      // Special handling for advance receipts
      if (payment.receiptNumber && payment.receiptNumber.startsWith('ADV-')) {
        // Extract invoice number from advance receipt (e.g., ADV-PUR25-26/12 -> PUR25-26/12)
        const targetInvoiceNo = payment.receiptNumber.replace('ADV-', '');
        const currentInvoiceNo = bill.number || bill.invoiceNumber || bill.id;
        return targetInvoiceNo === currentInvoiceNo;
      }
      
      // Check if payment has allocations for this invoice
      if (payment.allocations && Array.isArray(payment.allocations)) {
        return payment.allocations.some(allocation => 
          allocation.billId === bill.id && allocation.billType === 'purchase'
        );
      }
      
      // Check if payment has advance allocations for this invoice
      if (payment.advanceAllocations && Array.isArray(payment.advanceAllocations)) {
        return payment.advanceAllocations.some(allocation => 
          allocation.billId === bill.id
        );
      }
      
      // Check direct billId match
      if (payment.billId === bill.id) {
        return true;
      }
      
      return false;
    });
  };

  // Handle payment receipt preview
  const handlePreviewReceipt = (payment) => {
    setSelectedPaymentForReceipt(payment);
    setShowReceiptModal(true);
  };

  // Handle payment receipt preview from bill
  const handleViewPaymentReceipts = (bill) => {
    const billPayments = getBillPayments(bill);
    if (billPayments.length > 0) {
      setSelectedBillForReceipts(bill);
      setShowPaymentDetailsModal(true);
    }
  };

  // Print receipt
  const printReceipt = () => {
    if (receiptRef.current) {
      const printContents = receiptRef.current.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1000');
      printWindow.document.write('<html><head><title>Payment Receipt</title>');
      printWindow.document.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">');
      printWindow.document.write('<style>');
      printWindow.document.write('.print\\:hidden { display: none !important; }');
      printWindow.document.write('@media print { .print\\:hidden { display: none !important; } }');
      printWindow.document.write('.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:border { border-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:flex-row { flex-direction: row !important; }');
      printWindow.document.write('.print\\:text-sm { font-size: 0.875rem !important; }');
      printWindow.document.write('.print\\:text-xs { font-size: 0.75rem !important; }');
      printWindow.document.write('.print\\:text-lg { font-size: 1.125rem !important; }');
      printWindow.document.write('.print\\:text-xl { font-size: 1.25rem !important; }');
      printWindow.document.write('.print\\:text-2xl { font-size: 1.5rem !important; }');
      printWindow.document.write('.print\\:font-bold { font-weight: 700 !important; }');
      printWindow.document.write('.print\\:font-semibold { font-weight: 600 !important; }');
      printWindow.document.write('.print\\:text-center { text-align: center !important; }');
      printWindow.document.write('.print\\:text-right { text-align: right !important; }');
      printWindow.document.write('.print\\:text-left { text-align: left !important; }');
      printWindow.document.write('.print\\:p-4 { padding: 1rem !important; }');
      printWindow.document.write('.print\\:p-2 { padding: 0.5rem !important; }');
      printWindow.document.write('.print\\:p-1 { padding: 0.25rem !important; }');
      printWindow.document.write('.print\\:m-4 { margin: 1rem !important; }');
      printWindow.document.write('.print\\:m-2 { margin: 0.5rem !important; }');
      printWindow.document.write('.print\\:m-1 { margin: 0.25rem !important; }');
      printWindow.document.write('.print\\:border-b { border-bottom-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:w-full { width: 100% !important; }');
      printWindow.document.write('.print\\:h-auto { height: auto !important; }');
      printWindow.document.write('.print\\:max-w-none { max-width: none !important; }');
      printWindow.document.write('.print\\:shadow-none { box-shadow: none !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:text-black { color: black !important; }');
      printWindow.document.write('.print\\:text-gray-800 { color: #1f2937 !important; }');
      printWindow.document.write('.print\\:text-gray-600 { color: #4b5563 !important; }');
      printWindow.document.write('.print\\:text-gray-500 { color: #6b7280 !important; }');
      printWindow.document.write('.print\\:text-blue-600 { color: #2563eb !important; }');
      printWindow.document.write('.print\\:text-green-600 { color: #16a34a !important; }');
      printWindow.document.write('.print\\:text-red-600 { color: #dc2626 !important; }');
      printWindow.document.write('.print\\:text-yellow-600 { color: #ca8a04 !important; }');
      printWindow.document.write('.print\\:text-purple-600 { color: #9333ea !important; }');
      printWindow.document.write('.print\\:text-pink-600 { color: #db2777 !important; }');
      printWindow.document.write('.print\\:text-indigo-600 { color: #4f46e5 !important; }');
      printWindow.document.write('.print\\:text-teal-600 { color: #0d9488 !important; }');
      printWindow.document.write('.print\\:text-orange-600 { color: #ea580c !important; }');
      printWindow.document.write('.print\\:text-cyan-600 { color: #0891b2 !important; }');
      printWindow.document.write('.print\\:text-lime-600 { color: #65a30d !important; }');
      printWindow.document.write('.print\\:text-emerald-600 { color: #059669 !important; }');
      printWindow.document.write('.print\\:text-rose-600 { color: #e11d48 !important; }');
      printWindow.document.write('.print\\:text-violet-600 { color: #7c3aed !important; }');
      printWindow.document.write('.print\\:text-fuchsia-600 { color: #c026d3 !important; }');
      printWindow.document.write('.print\\:text-sky-600 { color: #0284c7 !important; }');
      printWindow.document.write('.print\\:text-slate-600 { color: #475569 !important; }');
      printWindow.document.write('.print\\:text-zinc-600 { color: #52525b !important; }');
      printWindow.document.write('.print\\:text-neutral-600 { color: #525252 !important; }');
      printWindow.document.write('.print\\:text-stone-600 { color: #57534e !important; }');
      printWindow.document.write('.print\\:text-red-500 { color: #ef4444 !important; }');
      printWindow.document.write('.print\\:text-green-500 { color: #22c55e !important; }');
      printWindow.document.write('.print\\:text-blue-500 { color: #3b82f6 !important; }');
      printWindow.document.write('.print\\:text-yellow-500 { color: #eab308 !important; }');
      printWindow.document.write('.print\\:text-purple-500 { color: #a855f7 !important; }');
      printWindow.document.write('.print\\:text-pink-500 { color: #ec4899 !important; }');
      printWindow.document.write('.print\\:text-indigo-500 { color: #6366f1 !important; }');
      printWindow.document.write('.print\\:text-teal-500 { color: #14b8a6 !important; }');
      printWindow.document.write('.print\\:text-orange-500 { color: #f97316 !important; }');
      printWindow.document.write('.print\\:text-cyan-500 { color: #06b6d4 !important; }');
      printWindow.document.write('.print\\:text-lime-500 { color: #84cc16 !important; }');
      printWindow.document.write('.print\\:text-emerald-500 { color: #10b981 !important; }');
      printWindow.document.write('.print\\:text-rose-500 { color: #f43f5e !important; }');
      printWindow.document.write('.print\\:text-violet-500 { color: #8b5cf6 !important; }');
      printWindow.document.write('.print\\:text-fuchsia-500 { color: #d946ef !important; }');
      printWindow.document.write('.print\\:text-sky-500 { color: #0ea5e9 !important; }');
      printWindow.document.write('.print\\:text-slate-500 { color: #64748b !important; }');
      printWindow.document.write('.print\\:text-zinc-500 { color: #71717a !important; }');
      printWindow.document.write('.print\\:text-neutral-500 { color: #737373 !important; }');
      printWindow.document.write('.print\\:text-stone-500 { color: #78716c !important; }');
      printWindow.document.write('@media print { img { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body>');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 100);
    }
  };

  // Generate PDF receipt
  const generatePDFReceipt = async () => {
    if (!selectedPaymentForReceipt) return;
    
    let jsPDF;
    if (window.jspdf && window.jspdf.jsPDF) {
      jsPDF = window.jspdf.jsPDF;
    } else if (window.jsPDF) {
      jsPDF = window.jsPDF;
    } else {
      try {
        const mod = await import('jspdf');
        jsPDF = (mod && (mod.jsPDF || mod.default)) || undefined;
      } catch (e) {
        console.error('Failed to load jsPDF:', e);
      }
    }
    if (!jsPDF) {
      alert('PDF generation library not available');
      return;
    }
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (2 * margin);
      let yPosition = margin;
      
      // Helper function to safely get text
      const safeText = (text) => text || '';
      

      
      // Company details
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(safeText(company.firmName) || 'Company Name', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      if (safeText(company.address)) {
        doc.text(safeText(company.address), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      if (safeText(company.city)) {
        doc.text(safeText(company.city), pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      if (safeText(company.gstin)) {
        doc.text(`GSTIN: ${safeText(company.gstin)}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 5;
      }
      
      yPosition += 10;
      
      // Receipt title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      doc.text('PAYMENT RECEIPT', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      // Receipt details
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      
      const receiptData = [
        ['Receipt Number:', safeText(selectedPaymentForReceipt.receiptNumber)],
        ['Date:', safeText(selectedPaymentForReceipt.paymentDate)],
        ['Party Name:', safeText(selectedPaymentForReceipt.partyName)],
        ['Payment Mode:', safeText(selectedPaymentForReceipt.paymentMode)],
        ['Amount:', formatCurrency(selectedPaymentForReceipt.totalAmount || 0, companyCurrency)],
      ];
      
      if (safeText(selectedPaymentForReceipt.reference)) {
        receiptData.push(['Reference:', safeText(selectedPaymentForReceipt.reference)]);
      }
      if (safeText(selectedPaymentForReceipt.notes)) {
        receiptData.push(['Notes:', safeText(selectedPaymentForReceipt.notes)]);
      }
      
      receiptData.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(label, margin, yPosition);
        doc.setFont(undefined, 'normal');
        doc.text(value, margin + 50, yPosition);
        yPosition += 8;
      });
      
      // Allocations
      if (selectedPaymentForReceipt.allocations && selectedPaymentForReceipt.allocations.length > 0) {
        yPosition += 10;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Payment Allocations:', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        selectedPaymentForReceipt.allocations.forEach((allocation, index) => {
          const billType = safeText(allocation.billType).toUpperCase();
          const billNumber = safeText(allocation.billNumber);
          const allocatedAmount = allocation.allocatedAmount || 0;
          doc.text(`${index + 1}. ${billType} ${billNumber}: ${formatCurrency(allocatedAmount, companyCurrency)}`, margin + 5, yPosition);
          yPosition += 6;
        });
      }
      
      // Advance allocations
      if (selectedPaymentForReceipt.advanceAllocations && selectedPaymentForReceipt.advanceAllocations.length > 0) {
        yPosition += 10;
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Advance Allocations:', margin, yPosition);
        yPosition += 10;
        
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        selectedPaymentForReceipt.advanceAllocations.forEach((advance, index) => {
          const amountUsed = advance.amountUsed || 0;
          doc.text(`${index + 1}. Advance Payment: ${formatCurrency(amountUsed, companyCurrency)}`, margin + 5, yPosition);
          yPosition += 6;
        });
      }
      
      // Footer
      yPosition = pageHeight - 30;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text('Thank you for your payment!', pageWidth / 2, yPosition, { align: 'center' });
      
      // Save PDF with initials and compact date
      const fmt = (d) => {
        try { const x = new Date(d); const day = String(x.getDate()); const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; const yr = x.getFullYear(); return `${day}${mon}${yr}`; } catch { return d; }
      };
      const fileName = `REC_${safeText(selectedPaymentForReceipt.receiptNumber)}_${fmt(selectedPaymentForReceipt.paymentDate)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const handleAddPayment = async (bill) => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('Cash');
    setPaymentReference('');
    setPaymentNotes('');
    setReceiptNumber('');
    
    // Generate receipt number
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    
    // Pre-fill with bill details
    setPaymentAmount(getBillOutstanding(bill).toString());
    
    // Store the bill information for payment
    setSelectedBillForPayment(bill);
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!selectedBillForPayment || !paymentAmount) {
      alert('Please fill in amount field.');
      return;
    }

    try {
      const paymentAmountNum = parseFloat(paymentAmount);
      const billOutstanding = getBillOutstanding(selectedBillForPayment);
      
      // Check if payment amount exceeds bill outstanding
      if (paymentAmountNum > billOutstanding) {
        alert(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(billOutstanding, companyCurrency)}). Please use the Payments page for amounts greater than the bill outstanding.`);
        return;
      }
      
      // First, try to allocate available advance
      const availableAdvance = getPartyAdvance(selectedBillForPayment.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let remainingPaymentAmount = paymentAmountNum;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill
        const advanceResult = allocateAdvanceToBill(selectedBillForPayment.party, billOutstanding, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          remainingPaymentAmount = paymentAmountNum - advanceUsed;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments);
          }
        }
      }
      
      // Simple bill payment - no excess amount handling since it's restricted above
      const paymentData = {
        receiptNumber: receiptNumber,
        paymentDate: paymentDate,
        partyId: selectedBillForPayment.party,
        partyName: getPartyName(selectedBillForPayment.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentMode,
        reference: paymentReference,
        notes: paymentNotes,
        type: 'purchase',
        paymentType: 'bill',
        billId: selectedBillForPayment.id,
        billNumber: selectedBillForPayment.number,
        allocations: [{
          billType: 'purchase',
          billId: selectedBillForPayment.id,
          billNumber: selectedBillForPayment.number,
          allocatedAmount: paymentAmountNum,
          billOutstanding: billOutstanding,
          isFullPayment: paymentAmountNum >= billOutstanding
        }],
        remainingAmount: 0, // No remaining amount since payment cannot exceed bill outstanding
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      await addDoc(paymentsRef, paymentData);
      
      // Reset payment form
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('Cash');
      setPaymentReference('');
      setPaymentNotes('');
      setReceiptNumber('');
      setSelectedBillForPayment(null);
      setShowPaymentModal(false);
      
      alert('Payment added successfully!');
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Error saving payment. Please try again.');
    }
  };

  // Totals - Calculate based on party GST type
  // Filter out empty rows for display calculations
  const filledRowsForDisplay = rows.filter(row => row.item && row.item.trim() !== '');
  const subtotal = roundTo2Decimals(filledRowsForDisplay.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0));
  
  // Get party GST type to determine GST calculation
  const partyObj = parties.find(p => p.id === party);
  const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
  
  let totalSGST = 0, totalCGST = 0, totalIGST = 0, grandTotal = 0;
  
  if (partyGstType === 'Regular') {
    // Calculate GST only for Regular parties
    totalSGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.sgst), 0);
    totalCGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.cgst), 0);
    totalIGST = filledRowsForDisplay.reduce((sum, row) => sum + calculateGSTAmount(row.amount, row.igst), 0);
    grandTotal = roundTo2Decimals(subtotal + totalSGST + totalCGST + totalIGST);
  } else {
    // For Composition/Unregistered parties, no GST calculation
    totalSGST = 0;
    totalCGST = 0;
    totalIGST = 0;
    grandTotal = roundTo2Decimals(subtotal); // Total equals subtotal (no GST added)
  }

  // Add print and PDF download handlers (copied from Sales.js, adapted):
  const handleInvoicePrint = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      return;
    }
    
    try {
      const temp = document.createElement('div');
      temp.innerHTML = invoiceRef.current.innerHTML;
      await inlineImagesForPrint(temp);
      
      const printContents = temp.innerHTML;
      const printWindow = window.open('', '', 'height=800,width=1000');
      
      if (!printWindow) {
        console.error('Print: Failed to open print window');
        alert('Please allow popups to print this document');
        return;
      }
      
      printWindow.document.write('<html><head><title>Purchase Bill</title>');
      printWindow.document.write('<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">');
      printWindow.document.write('<style>');
      printWindow.document.write('html,body{margin:0;padding:0;background:#fff;}');
      printWindow.document.write('@media print{html,body{width:210mm!important;max-width:210mm!important;box-sizing:border-box!important;}}');
      printWindow.document.write('@page{size:A4;margin:8mm;}');
      printWindow.document.write('.print\\:hidden { display: none !important; }');
      printWindow.document.write('@media print { .print\\:hidden { display: none !important; } }');
      printWindow.document.write('.print\\:grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }');
      printWindow.document.write('.print\\:bg-white { background-color: white !important; }');
      printWindow.document.write('.print\\:border { border-width: 1px !important; }');
      printWindow.document.write('.print\\:border-gray-300 { border-color: #d1d5db !important; }');
      printWindow.document.write('.print\\:flex-row { flex-direction: row !important; }');
      printWindow.document.write('.print\\:bg-yellow-100 { background-color: #fef3c7 !important; }');
      printWindow.document.write('.print\\:bg-green-100 { background-color: #dcfce7 !important; }');
      printWindow.document.write('.print\\:transform-none { transform: none !important; }');
      printWindow.document.write('@media print { .grid { display: grid !important; } }');
      printWindow.document.write('@media print { .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }');
      printWindow.document.write('@media print { .flex { display: flex !important; } }');
      printWindow.document.write('@media print { .flex-row { flex-direction: row !important; } }');
      printWindow.document.write('@media print { [class*="print:hidden"] { display: none !important; } }');
      printWindow.document.write('@media print { button { display: none !important; } }');
      printWindow.document.write('@media print { .sticky { position: static !important; } }');
      printWindow.document.write('</style>');
      printWindow.document.write('</head><body style="background:white;">');
      printWindow.document.write(printContents);
      printWindow.document.write('</body></html>');
      printWindow.document.close();
      printWindow.focus();
      
      const ensureImages = () => {
        try {
          const imgs = Array.from(printWindow.document.images || []);
          if (imgs.length === 0) return Promise.resolve();
          return Promise.all(imgs.map(img => new Promise(resolve => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          })));
        } catch (_) {
          return Promise.resolve();
        }
      };
      
      printWindow.onload = async () => {
        try {
          await ensureImages();
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 300);
        } catch (error) {
          console.error('Print: Error in print window:', error);
          printWindow.close();
        }
      };
      
    } catch (error) {
      alert('Error preparing print. Please try again.');
    }
  };

  // Print function for success modal
  const handleSuccessModalPrint = () => {
    const bill = bills.find(b => b.id === savedBillId);
    if (!bill) return;
    
    // Enhance bill data with proper measurement units
    const enhancedBill = {
      ...bill,
      rows: (bill.rows || []).map(row => {
        if (row.item) {
          const itemObj = items.find(it => it.id === row.item);
          if (itemObj) {
            return {
              ...row,
              description: itemObj.itemName || row.description || '',
              unit: itemObj.quantityMeasurement || row.unit || '',
            };
          }
        }
        return row;
      }),
    };
    
    setInvoiceBill(enhancedBill);
    setShowInvoiceModal(true);
    setShowSuccessModal(false);
  };

  // Reset form for creating new bill
  const resetFormToInitialState = () => {
    setBillNumber('');
    setBillDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setNotes('');
    setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
    setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
    setCurrentMobileItemIndex(0);
    setEditingBillId(null);
    setItemSearchTextByRow({});
  };

  // useEffect to trigger form reset when success modal is shown
  useEffect(() => {
    if (showSuccessModal) {
      // Small delay to ensure modal is fully rendered before resetting
      const timer = setTimeout(() => {
        resetFormToInitialState();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  // PDF download handler
  const handleInvoiceDownload = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      alert('Document not ready. Please wait for the document to load.');
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      alert('Document content is not ready. Please wait for the document to load completely.');
      return;
    }
    
    // Show loading state
    const button = document.querySelector('button[onClick="handleInvoiceDownload"]') || 
                  document.querySelector('button[onclick="handleInvoiceDownload"]') ||
                  document.querySelector('[onClick="handleInvoiceDownload"]') ||
                  document.querySelector('[onclick="handleInvoiceDownload"]');
    
    let originalText = 'Save as PDF';
    if (button) {
      originalText = button.textContent || 'Save as PDF';
      button.textContent = 'Generating PDF...';
      button.disabled = true;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for PDF generation...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2pdf is available
      let html2pdf = window.html2pdf;
      
      if (!html2pdf) {
        try {
          // Try to load html2pdf
          const module = await import('html2pdf.js');
          html2pdf = module.default || module;
          window.html2pdf = html2pdf; // Store for future use
        } catch (importError) {
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
              script.onload = () => {
                html2pdf = window.html2pdf;
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            html2pdf = null; // Will trigger jsPDF fallback
          }
        }
      }
      
      // Test if html2pdf is working
      if (html2pdf && typeof html2pdf !== 'function') {
        html2pdf = null; // Will trigger jsPDF fallback
      }
      
      // Wait a bit for any dynamic content to render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a temporary container and clone the content
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      
      if (tempContainer.innerHTML.length < 100) {
        alert('Document content is empty. Please try again.');
        return;
      }
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      
      // Add delay to ensure images are fully loaded and DOM is ready for PDF generation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill?.number || 'Bill'}_${fmt(invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date)}.pdf`;
      
      // Generate PDF with optimized settings
      if (html2pdf) {
        const worker = html2pdf().from(tempContainer).set({
          margin: 0.5,
          filename: filename,
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            allowTaint: true,
            backgroundColor: '#ffffff',
            imageTimeout: 8000,
            removeContainer: true
          },
          jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
        });
        
        await worker.save();
      } else {
        // Fallback to jsPDF
        const { jsPDF } = window.jspdf;
        const doc = new jsPDF('p', 'in', 'a4');
        
        // Simple text-based PDF as fallback
        doc.setFontSize(16);
        doc.text(`${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`, 1, 1);
        
        doc.setFontSize(12);
        doc.text(`Date: ${invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date || 'N/A'}`, 1, 1.5);
        doc.text(`Party: ${parties.find(p => p.id === invoiceBill?.party)?.firmName || 'N/A'}`, 1, 2);
        doc.text(`Total Amount: ${formatCurrency(invoiceBill?.amount || 0, companyCurrency)}`, 1, 2.5);
        
        // Add items
        let yPos = 3;
        (invoiceBill?.rows || invoiceBill?.items || []).forEach((item, index) => {
          const itemName = items.find(it => it.id === item.item)?.itemName || item.item || 'Unknown Item';
          doc.text(`${index + 1}. ${itemName} - Qty: ${item.qty}, Rate: ${formatCurrency(item.rate, companyCurrency)}, Amount: ${formatCurrency(item.amount, companyCurrency)}`, 1, yPos);
          yPos += 0.3;
        });
        
        doc.save(filename);
      }
          } catch (error) {
        console.error('PDF Download: Error generating PDF:', error);
        const errorMessage = `Error generating PDF: ${error.message || error.toString()}. Please try again.`;
        console.error('PDF Download: Full error details:', errorMessage);
        alert(errorMessage);
      } finally {
        // Restore button state
        if (button) {
          button.textContent = originalText;
          button.disabled = false;
        }
      }
  };

  // Share: link to open this purchase bill directly in preview

  const handleInvoiceWheel = (e) => {
    // Only zoom when Ctrl key is held down, otherwise allow normal scrolling
    if (e.ctrlKey && showInvoiceModal) {
      e.preventDefault();
      setInvoiceZoom(z => {
        let next = z + (e.deltaY < 0 ? 0.1 : -0.1);
        next = Math.max(0.5, Math.min(2, next));
        return Math.round(next * 100) / 100;
      });
    }
    // If Ctrl is not held, let the browser handle normal scrolling
  };

  // Optimized PDF Share Handler - generates PDF as blob and shares via system
  const handleInvoiceSharePdf = async () => {
    console.log('PDF Share: Starting PDF generation...');
    
    if (!invoiceRef.current || !invoiceBill) {
      console.error('PDF Share: Missing invoiceRef or invoiceBill');
      alert('Document not ready. Please wait for the document to load.');
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      console.error('PDF Share: Document content not ready');
      alert('Document content is not ready. Please wait for the document to load completely.');
      return;
    }
    
    console.log('PDF Share: Document content ready, proceeding...');
    
    // Show loading state
    const shareButton = document.querySelector('button[onClick="handleInvoiceSharePdf"]') ||
                       document.querySelector('button[onclick="handleInvoiceSharePdf"]') ||
                       document.querySelector('[onClick="handleInvoiceSharePdf"]') ||
                       document.querySelector('[onclick="handleInvoiceSharePdf"]') ||
                       document.querySelector('.PurchaseShareButton button') ||
                       Array.from(document.querySelectorAll('button')).find(btn => btn.textContent.includes('Share'));
    
    let originalText = 'Share as PDF';
    if (shareButton) {
      originalText = shareButton.textContent || 'Share as PDF';
      shareButton.textContent = 'Generating PDF...';
      shareButton.disabled = true;
      console.log('PDF Share: Button state updated');
    } else {
      console.warn('PDF Share: Could not find share button to update');
    }
    
    let tempContainer;
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for PDF share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2pdf is available
      let html2pdf = window.html2pdf;
      console.log('PDF Share: html2pdf available:', !!html2pdf);
      
      if (!html2pdf) {
        console.log('PDF Share: html2pdf not found, attempting to load...');
        try {
          // Try to load html2pdf
          const module = await import('html2pdf.js');
          html2pdf = module.default || module;
          window.html2pdf = html2pdf; // Store for future use
          console.log('PDF Share: html2pdf loaded from module');
        } catch (importError) {
          console.log('PDF Share: Module import failed, trying CDN...');
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
              script.onload = () => {
                html2pdf = window.html2pdf;
                console.log('PDF Share: html2pdf loaded from CDN');
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            console.error('PDF Share: CDN loading failed:', cdnError);
            alert('PDF generation library not available. Please try again.');
            return;
          }
        }
      }
      
      // Test if html2pdf is working
      if (html2pdf && typeof html2pdf !== 'function') {
        console.error('PDF Share: html2pdf is not a function');
        alert('PDF generation library not available. Please try again.');
        return;
      }
      
      console.log('PDF Share: html2pdf is ready, creating container...');
      
      // Create a temporary container and clone the existing content; attach offscreen for proper layout and image loading
      tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);
      
      if (tempContainer.innerHTML.length < 100) {
        console.error('PDF Share: Cloned content is empty');
        alert('Document content is empty. Please try again.');
        return;
      }
      
      console.log('PDF Share: Content cloned successfully, processing images...');
      
      // Hide print:hidden elements
      const hiddenElements = tempContainer.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => el.style.display = 'none');

      // Fast image processing for PDF generation
      const imgs = Array.from(tempContainer.querySelectorAll('img'));
      console.log('PDF Share: Found', imgs.length, 'images to process');
      
      const imagePromises = imgs.map(async (img) => {
        const src = img.getAttribute('src');
        if (!src) return;
        
        try {
          const imageData = await convertImageToDataUrl(src);
          if (imageData) {
            img.setAttribute('src', imageData);
            img.removeAttribute('crossorigin');
          } else if (window.imageManager) {
            const imageRecord = await window.imageManager.getImage(src);
            if (imageRecord?.data) {
              img.setAttribute('src', imageRecord.data);
              img.removeAttribute('crossorigin');
            }
          }
        } catch (error) {
          console.warn('PDF Share: Image processing failed for', src, error);
          img.style.display = 'none';
        }
      });
      
      // Process all images in parallel
      await Promise.all(imagePromises);
      console.log('PDF Share: Images processed, generating PDF...');
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill?.number || 'Bill'}_${fmt(invoiceBill?.billDate || invoiceBill?.invoiceDate || invoiceBill?.date)}.pdf`;
      console.log('PDF Share: Filename generated:', filename);
      
      // Generate PDF with optimized settings
      const worker = html2pdf().from(tempContainer).set({
        margin: 0.5,
        filename: filename,
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          allowTaint: true,
          backgroundColor: '#ffffff',
          imageTimeout: 10000, // Increased timeout for mobile
          removeContainer: true,
          logging: false, // Disable logging for better performance
          foreignObjectRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: tempContainer.scrollWidth,
          windowHeight: tempContainer.scrollHeight
        },
        jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
      });
      
      console.log('PDF Share: PDF worker created, generating blob...');
      const pdfBlob = await worker.outputPdf('blob');
      console.log('PDF Share: PDF blob generated, size:', pdfBlob.size);
      
      // Share PDF via system share or download
      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], filename, { type: 'application/pdf' })] })) {
          console.log('PDF Share: Attempting system share...');
          await navigator.share({
            title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
            text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
            files: [new File([pdfBlob], filename, { type: 'application/pdf' })]
          });
          console.log('PDF Share: System share successful');
        } else {
          console.log('PDF Share: System share not available, downloading...');
          // Download fallback
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          console.log('PDF Share: Download successful');
        }
      } catch (shareError) {
        console.warn('PDF Share: System share failed, downloading instead:', shareError);
        // Download fallback if sharing fails
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        console.log('PDF Share: Download fallback successful');
      }
      
      console.log('PDF Share: PDF generation completed successfully');
    } catch (error) {
      console.error('PDF Share: Error generating PDF:', error);
      const errorMessage = `Error generating PDF: ${error.message || error.toString()}. Please try again.`;
      alert(errorMessage);
    } finally {
      try { if (tempContainer && tempContainer.parentNode) document.body.removeChild(tempContainer); } catch {}
      // Restore button state
      if (shareButton) {
        shareButton.textContent = originalText;
        shareButton.disabled = false;
        console.log('PDF Share: Button state restored');
      }
    }
  };

  // Optimized Image Share Handler - generates image as blob and shares via system
  const handleInvoiceShareImage = async () => {
    console.log('Image Share: Starting image generation...');
    
    if (!invoiceRef.current || !invoiceBill) {
      console.error('Image Share: Missing invoiceRef or invoiceBill');
      alert('Document not ready. Please wait for the document to load.');
      return;
    }
    
    // Check if we have the required content
    if (!invoiceRef.current.innerHTML || invoiceRef.current.innerHTML.length < 100) {
      console.error('Image Share: Document content not ready');
      alert('Document content is not ready. Please wait for the document to load completely.');
      return;
    }
    
    console.log('Image Share: Document content ready, proceeding...');
    
    // Show loading state
    const shareButton = document.querySelector('button[onClick="handleInvoiceShareImage"]') ||
                       document.querySelector('button[onclick="handleInvoiceShareImage"]') ||
                       document.querySelector('[onClick="handleInvoiceShareImage"]') ||
                       document.querySelector('[onclick="handleInvoiceShareImage"]') ||
                       document.querySelector('.PurchaseShareButton button') ||
                       Array.from(document.querySelectorAll('button')).find(btn => btn.textContent.includes('Share'));
    
    let originalText = 'Share as Image';
    if (shareButton) {
      originalText = shareButton.textContent || 'Share as Image';
      shareButton.textContent = 'Generating Image...';
      shareButton.disabled = true;
      console.log('Image Share: Button state updated');
    } else {
      console.warn('Image Share: Could not find share button to update');
    }
    
    let tempContainer;
    try {
      // Preload images before image generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      const bankDetails = {
        sealUrl: currentCompany?.sealUrl || currentCompany?.details?.sealUrl,
        signUrl: currentCompany?.signUrl || currentCompany?.details?.signUrl
      };
      
      console.log('Preloading images for image share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      // Check if html2canvas is available
      let html2canvas = window.html2canvas;
      console.log('Image Share: html2canvas available:', !!html2canvas);
      
      if (!html2canvas) {
        console.log('Image Share: html2canvas not found, attempting to load...');
        try {
          // Try to load html2canvas
          const module = await import('html2canvas');
          html2canvas = module.default || module;
          window.html2canvas = html2canvas; // Store for future use
          console.log('Image Share: html2canvas loaded from module');
        } catch (importError) {
          console.log('Image Share: Module import failed, trying CDN...');
          // Try to load from CDN as fallback
          try {
            await new Promise((resolve, reject) => {
              const script = document.createElement('script');
              script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
              script.onload = () => {
                html2canvas = window.html2canvas;
                console.log('Image Share: html2canvas loaded from CDN');
                resolve();
              };
              script.onerror = reject;
              document.head.appendChild(script);
            });
          } catch (cdnError) {
            console.error('Image Share: CDN loading failed:', cdnError);
            alert('Image generation library not available. Please try again.');
            return;
          }
        }
      }
      
      // Test if html2canvas is working
      if (html2canvas && typeof html2canvas !== 'function') {
        console.error('Image Share: html2canvas is not a function');
        alert('Image generation library not available. Please try again.');
        return;
      }
      
      console.log('Image Share: html2canvas is ready, creating container...');
      
      // Create a temporary container and clone the existing content; attach offscreen for accurate layout
      tempContainer = document.createElement('div');
      tempContainer.innerHTML = invoiceRef.current.innerHTML;
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '-9999px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.width = '210mm';
      document.body.appendChild(tempContainer);
      
      if (tempContainer.innerHTML.length < 100) {
        console.error('Image Share: Cloned content is empty');
        alert('Template rendering failed. Please try again.');
        return;
      }
      
      console.log('Image Share: Content cloned successfully, generating image...');
      
      // Generate image with optimized settings for mobile
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        imageTimeout: 10000, // Increased timeout for mobile
        logging: false, // Disable logging for better performance
        foreignObjectRendering: true,
        scrollX: 0,
        scrollY: 0,
        windowWidth: tempContainer.scrollWidth,
        windowHeight: tempContainer.scrollHeight
      });
      
      console.log('Image Share: Canvas generated, converting to blob...');
      
      canvas.toBlob(async (blob) => {
        const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')}_${invoiceBill?.number || 'Bill'}.png`;
        console.log('Image Share: Blob created, filename:', filename);
        
        try {
          // Try to share via system share
          if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: 'image/png' })] })) {
            console.log('Image Share: Attempting system share...');
            await navigator.share({
              title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
              text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill?.number || 'Bill'}`,
              files: [new File([blob], filename, { type: 'image/png' })]
            });
            console.log('Image Share: System share successful');
          } else {
            console.log('Image Share: System share not available, downloading...');
            // Download fallback
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
            console.log('Image Share: Download successful');
          }
        } catch (shareError) {
          console.warn('Image Share: System share failed, downloading instead:', shareError);
          // Download fallback if sharing fails
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          console.log('Image Share: Download fallback successful');
        }
        
        console.log('Image Share: Image generation completed successfully');
      }, 'image/png');
    } catch (error) {
      console.error('Image Share: Error generating image:', error);
      const errorMessage = `Error generating image: ${error.message || error.toString()}. Please try again.`;
      console.error('Image Share: Full error details:', errorMessage);
      alert(errorMessage);
    } finally {
      try { if (tempContainer && tempContainer.parentNode) document.body.removeChild(tempContainer); } catch {}
      // Restore button state
      if (shareButton) {
        shareButton.textContent = originalText;
        shareButton.disabled = false;
        console.log('Image Share: Button state restored');
      }
    }
  };






  // UI rendering (similar to Sales)
    return (
    <div className="min-h-screen bg-gray-100 flex flex-col py-6 sm:py-10 mobile-content">
      <style>
        {`
          /* Fix datalist dropdown z-index issues */
          input[list]::-webkit-calendar-picker-indicator {
            z-index: 9999 !important;
          }
          
          /* Ensure datalist dropdown appears above other elements */
          input[list] + datalist {
            z-index: 9999 !important;
            position: relative !important;
          }
          
          /* Fix table cell positioning context for dropdowns */
          table td {
            position: relative;
          }
          
          /* Ensure dropdowns appear above other content */
          select {
            z-index: 10 !important;
            position: relative;
          }
          
          /* Fix pagination dropdown overlap */
          .pagination-select {
            z-index: 20 !important;
          }
          
          /* Custom dropdown styling */
          select[style*="backgroundImage"] {
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          
          select[style*="backgroundImage"]::-ms-expand {
            display: none;
          }
          
          table td .relative {
            z-index: auto;
          }
          
          /* Ensure dropdowns in table cells appear above */
          table td input[list] {
            position: relative;
            z-index: 10;
          }
        `}
      </style>
      {errorMessage && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-6 py-3 rounded shadow-lg z-50 flex items-center gap-4">
          <span>{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="ml-4 text-red-700 font-bold text-lg">&times;</button>
        </div>
      )}
      {/* Tabs Section - Full width */}
      <div className="w-full mb-4 sm:mb-6 px-4 sm:px-0">
        <div className="w-full">
          {/* Desktop: Full-width Tabs, Mobile: Dropdown */}
          <div className="mb-2">
            {/* Desktop View - Full-width Tabs */}
            <div className="hidden sm:block">
            <div className="flex bg-gray-100 rounded-lg p-1 justify-center">
                {docTypeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDocType(opt.value)}
                  className={`flex-initial flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                      docType === opt.value
                        ? 'bg-white text-blue-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    {/* Icon for each document type */}
                    {opt.value === 'purchaseBill' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    )}
                    {opt.value === 'purchaseOrder' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    )}
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile View - Dropdown */}
            <div className="sm:hidden flex items-center justify-center">
              <div className="relative">
                <button
                  onClick={() => setShowDocTypeDropdown(!showDocTypeDropdown)}
                  className="bg-white rounded-lg shadow-md px-4 py-2 flex items-center gap-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all duration-200 min-w-[140px] justify-center"
                >
                  {/* Icon for current document type */}
                  {docType === 'purchaseBill' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {docType === 'purchaseOrder' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                  <span>{docTypeOptions.find(opt => opt.value === docType)?.label || 'PURCHASE BILL'}</span>
                  <svg className={`w-4 h-4 transition-transform duration-200 ${showDocTypeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showDocTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    {docTypeOptions.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setDocType(opt.value);
                          setShowDocTypeDropdown(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm font-medium transition-all duration-200 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg ${
                          docType === opt.value
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {/* Icon for each document type */}
                        {opt.value === 'purchaseBill' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        {opt.value === 'purchaseOrder' && (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                        )}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
        {/* Quick Items Section - 20-60-20 Layout - Full Width */}
        <div className="mb-8 w-full px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-12 gap-4 items-stretch w-full">
            {/* Left Panel - Most Used Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-blue-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Most Used Items</h3>

              <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                {mostUsedItems.map((itemData) => {
                  const item = items.find(it => it.id === itemData.id);
                  if (!item) return null;
                  
                  return (
                    <button
                      key={itemData.itemId || itemData.id}
                      onClick={() => handleQuickItemClick(itemData)}
                      className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-md p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                      title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                    >
                      <div className="font-semibold text-sm text-blue-900 truncate">
                        {itemData.itemName || itemData.name || 'Unknown Item'}
                      </div>
                      <div className="text-xs text-blue-700 font-medium">
                        {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 1}
                      </div>
                      <div className="text-xs text-blue-600">
                        Used {itemData.count} times
                      </div>
                    </button>
                  );
                })}
              </div>
              {mostUsedItems.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  {t('noItemsUsedYet')}
                </div>
              )}
            </div>

            {/* Center Panel - Purchase Entry Form (60%) */}
            <div className="col-span-12 lg:col-span-8 bg-white rounded-lg shadow-md p-4 sm:p-6">
        {/* Fixed Header - Mobile & Desktop */}
        <div className="bg-gray-50 rounded-lg p-3 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex-1">
              <div className="text-xs text-gray-500 mb-1">{docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Purchase Bill Number'}</div>
              <div className="text-sm font-semibold text-gray-800">{billNumber || 'Auto'}</div>
            </div>
            <div className="flex-1 ml-4">
              <div className="text-xs text-gray-500 mb-1">Date</div>
              <div className="text-sm font-semibold text-gray-800">{billDate}</div>
            </div>
            <button
              onClick={() => setShowMobileEditModal(true)}
              className="ml-2 p-1 text-gray-600 hover:text-blue-600"
              title="Edit Details"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Party Selection - Mobile & Desktop */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('partySeller')}</label>
          <div className="relative party-dropdown-container">
            <input
              type="text"
              value={partySearchText}
              onChange={(e) => {
                setPartySearchText(e.target.value);
                if (e.target.value === '') {
                  setParty('');
                  setSelectedPartyForQuickItems('');
                }
              }}
              onFocus={() => setShowPartyDropdown(true)}
              className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
              placeholder="Search or type new party name..."
            />
            {showPartyDropdown && (
              <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                {filteredParties.map(p => (
                  <button
                    key={p.id}
                    onClick={() => {
                      setParty(p.id);
                      setSelectedPartyForQuickItems(p.id);
                      setPartySearchText(p.firmName);
                      setShowPartyDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                  >
                    {p.firmName}
                  </button>
                ))}
                {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                  <button
                    onClick={() => {
                      setNewPartyName(partySearchText);
                      setShowAddPartyModal(true);
                      setShowPartyDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                  >
                    + Add "{partySearchText}" as new party
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        
        
        {/* GST Type Info */}
        {party && (
          <>
            {/* Desktop View - Full Box */}
            <div className="hidden md:block mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                GST Type: <span className="font-bold">{getPartyGstType(party)}</span>
                {getPartyGstType(party) === 'Regular' ? ' - GST fields are available for calculation' : ' - GST fields are hidden (no GST calculation)'}
              </span>
            </div>
            {getPartyGstType(party) === 'Regular' && (
              <div className="mt-2 text-xs text-blue-700">
                <p>• For Regular parties, GST calculation is applicable</p>
                <p>• GST fields are available for calculation</p>
                <p>• Regular GST template will be used for printing</p>
              </div>
            )}
            {getPartyGstType(party) !== 'Regular' && (
              <div className="mt-2 text-xs text-blue-700">
                <p>• For {getPartyGstType(party)} parties, GST calculation is not applicable</p>
                <p>• Bill amount will be entered as received from supplier</p>
                <p>• Total amount will be the same as subtotal (no GST added)</p>
                <p>• Non-GST template will be used for printing</p>
              </div>
            )}
            </div>

            {/* Mobile View - Icon Dropdown */}
            <div className="md:hidden mb-4 relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-bold text-gray-900">Items</h3>
                  <div className="relative">
                    <button
                      onClick={() => setShowGstInfoDropdown(!showGstInfoDropdown)}
                      className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-full transition-colors"
                      title="GST Information"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                      
                    {/* Dropdown Content */}
                    {showGstInfoDropdown && (
                      <div className="absolute left-0 top-full mt-2 w-80 bg-white border border-blue-200 rounded-lg shadow-lg z-50">
                        <div className="p-3 bg-blue-50 border-b border-blue-200 rounded-t-lg">
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-sm font-medium text-blue-800">
                              GST Type: <span className="font-bold">{getPartyGstType(party)}</span>
                              {getPartyGstType(party) === 'Regular' ? ' - GST fields are available for calculation' : ' - GST fields are hidden (no GST calculation)'}
                            </span>
                          </div>
                        </div>
                        <div className="p-3">
                          {getPartyGstType(party) === 'Regular' && (
                            <div className="text-xs text-blue-700 space-y-1">
                              <p>• For Regular parties, GST calculation is applicable</p>
                              <p>• GST fields are available for calculation</p>
                              <p>• Regular GST template will be used for printing</p>
          </div>
                          )}
                          {getPartyGstType(party) !== 'Regular' && (
                            <div className="text-xs text-blue-700 space-y-1">
                              <p>• For {getPartyGstType(party)} parties, GST calculation is not applicable</p>
                              <p>• Bill amount will be entered as received from supplier</p>
                              <p>• Total amount will be the same as subtotal (no GST added)</p>
                              <p>• Non-GST template will be used for printing</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Quick Item Button */}
                <button
                  ref={quickItemButtonRef}
                  onClick={(e) => {
                    const position = calculateModalPosition(e.currentTarget);
                    setQuickItemModalPosition(position);
                    setShowQuickItemsModal(true);
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-1 px-3 rounded-md text-sm flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Quick Item
                </button>
              </div>
            </div>
          </>
        )}
        
        {/* Desktop Table View */}
        <div className="hidden md:block overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 mb-4 max-h-[600px] custom-scrollbar touch-scrollbar">
          <table ref={tableRef} className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50">
                                    <tr>
                <th className="px-2 py-1">ITEM</th>
                <th className="px-2 py-1">MEASUREMENT / NUMBERS</th>
                <th className="px-2 py-1">QTY</th>
                <th className="px-2 py-1">RATE</th>
                <th className="px-2 py-1">DISCOUNT</th>
                <th className="px-2 py-1">AMOUNT</th>
                {getPartyGstType(party) === 'Regular' && (
                  <>
                    <th className="px-2 py-1">SGST</th>
                    <th className="px-2 py-1">CGST</th>
                    <th className="px-2 py-1">IGST</th>
                  </>
                )}
                <th className="px-2 py-1">TOTAL</th>
                <th className="px-2 py-1">REMOVE</th>
                                    </tr>
                                </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1">
                      <div className="relative">
                        <input
                          type="text"
                          list={`p-items-list-${idx}`}
                          className="border border-gray-300 rounded-md p-1 w-48 relative"
                          style={{ zIndex: 1 }}
                          placeholder={t('selectItem')}
                          value={
                            itemSearchTextByRow[idx] !== undefined
                              ? itemSearchTextByRow[idx]
                              : (items.find(it => it.id === row.item)?.itemName || '')
                          }
                          onChange={(e) => {
                            const text = e.target.value;
                            setItemSearchTextByRow(prev => ({ ...prev, [idx]: text }));
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              return;
                            }
                            
                            const lower = text.toLowerCase();
                            const match = items.find(it =>
                              (it.itemName || '').toLowerCase() === lower ||
                              (it.code || '').toLowerCase() === lower ||
                              (it.hsn || it.hsnCode || '').toLowerCase() === lower
                            );
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: match.itemName }));
                            }
                          }}
                          onBlur={(e) => {
                            const text = e.target.value;
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: '' }));
                              return;
                            }
                            
                            const lower = text.toLowerCase();
                            const match = items.find(it =>
                              (it.itemName || '').toLowerCase().startsWith(lower) ||
                              (it.code || '').toLowerCase().startsWith(lower) ||
                              (it.hsn || it.hsnCode || '').toLowerCase().startsWith(lower)
                            );
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: match.itemName }));
                            }
                          }}
                        />
                        <datalist id={`p-items-list-${idx}`}>
                          {items.slice(0, 200).map(it => (
                            <option key={it.id} value={it.itemName} />
                          ))}
                        </datalist>
                      </div>
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex flex-col">
                      <input
                        type="text"
                        value={row.qtyExpr}
                        onChange={e => handleRowChange(idx, 'qtyExpr', e.target.value)}
                        placeholder="e.g. 5x3x2 or 10*2"
                        className="border border-gray-300 rounded-md p-1 w-40"
                      />
                      {row.qtyDisplay && (
                        <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={row.qty} min={0} readOnly
                      className="border border-gray-300 rounded-md p-1 w-16 bg-gray-100" />
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={row.rate} min={0} onChange={e => handleRowChange(idx, 'rate', e.target.value)}
                      className="border border-gray-300 rounded-md p-1 w-16" />
                  </td>
                  <td className="px-2 py-1">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        value={row.lineDiscountValue}
                        onChange={e => handleRowChange(idx, 'lineDiscountValue', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 w-20"
                        placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                      />
                      <select
                        value={row.lineDiscountType || 'amount'}
                        onChange={e => handleRowChange(idx, 'lineDiscountType', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 relative z-10 w-12 text-center appearance-none cursor-pointer bg-white"
                        style={{
                          backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 4px center',
                          backgroundSize: '12px',
                          paddingRight: '20px'
                        }}
                      >
                        <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                        <option value="percent">%</option>
                      </select>
                    </div>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={row.amount} min={0} readOnly
                      className="border border-gray-300 rounded-md p-1 w-20 bg-gray-100" />
                  </td>
                  {getPartyGstType(party) === 'Regular' && (
                    <>
                      <td className="px-2 py-1">
                        <input type="number" value={row.sgst} min={0} onChange={e => handleRowChange(idx, 'sgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={row.cgst} min={0} onChange={e => handleRowChange(idx, 'cgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                      <td className="px-2 py-1">
                        <input type="number" value={row.igst} min={0} onChange={e => handleRowChange(idx, 'igst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                      </td>
                    </>
                  )}
                  <td className="px-2 py-1">
                    <input type="number" value={row.total} min={0} readOnly
                      className="border border-gray-300 rounded-md p-1 w-20 bg-green-50 font-semibold" />
                  </td>
                  <td className="px-2 py-1">
                    <button type="button" onClick={() => removeRow(idx)} className="text-red-600 font-bold px-2 py-1 rounded hover:bg-red-100">X</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
          </table>
        </div>
        
        {/* Mobile Card View - Carousel Style */}
        <div className="md:hidden mb-4">
          {/* Carousel Container */}
          <div 
            className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm transition-all duration-200 ease-in-out"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* Swipe Instructions */}
            <div className="text-center text-xs text-gray-500 mb-2">
              Swipe left/right to navigate between items
            </div>
            
            {/* Navigation Header */}
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPreviousItem}
                  disabled={currentMobileItemIndex === 0}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <h4 className="text-sm font-semibold text-gray-800">Item #{currentMobileItemIndex + 1}</h4>
                <button
                  onClick={goToNextItem}
                  disabled={currentMobileItemIndex === rows.length - 1}
                  className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              <button
                onClick={() => removeRow(currentMobileItemIndex)}
                className="text-red-600 hover:text-red-800 text-lg"
              >
                🗑️
              </button>
            </div>

            {/* Current Item Card */}
            {rows[currentMobileItemIndex] && (() => {
              const row = rows[currentMobileItemIndex];
              const index = currentMobileItemIndex;
              return (
              <div key={currentMobileItemIndex}>
              
              {/* Item Selection */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Item</label>
                <input
                  type="text"
                  list={`mobile-items-list-${index}`}
                  value={itemSearchTextByRow[index] || ''}
                  onChange={(e) => {
                    const text = e.target.value;
                    setItemSearchTextByRow(prev => ({ ...prev, [index]: text }));
                    
                    if (!text.trim()) {
                        handleRowChange(currentMobileItemIndex, 'item', '');
                      return;
                    }
                    
                    const lower = text.toLowerCase();
                    const match = items.find(it =>
                      (it.itemName || '').toLowerCase() === lower ||
                      (it.code || '').toLowerCase() === lower ||
                      (it.hsn || it.hsnCode || '').toLowerCase() === lower
                    );
                    if (match) {
                        handleRowChange(currentMobileItemIndex, 'item', match.id);
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: match.itemName }));
                    }
                  }}
                  onBlur={(e) => {
                    const text = e.target.value;
                    
                    if (!text.trim()) {
                        handleRowChange(currentMobileItemIndex, 'item', '');
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: '' }));
                      return;
                    }
                    
                    const lower = text.toLowerCase();
                    const match = items.find(it =>
                      (it.itemName || '').toLowerCase().startsWith(lower) ||
                      (it.code || '').toLowerCase().startsWith(lower) ||
                      (it.hsn || it.hsnCode || '').toLowerCase().startsWith(lower)
                    );
                    if (match) {
                        handleRowChange(currentMobileItemIndex, 'item', match.id);
                        setItemSearchTextByRow(prev => ({ ...prev, [currentMobileItemIndex]: match.itemName }));
                    }
                  }}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  placeholder="Search item..."
                />
                <datalist id={`mobile-items-list-${index}`}>
                  {items.map(item => (
                    <option key={item.id} value={item.itemName || item.name}>
                      {item.itemName || item.name} - {formatCurrency(item.rate || item.purchasePrice || 0, companyCurrency)}
                    </option>
                  ))}
                </datalist>
              </div>
              
              {/* Measurement / Numbers Row */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Measurement / Numbers</label>
                <div className="flex flex-col">
                  <input
                    type="text"
                    value={row.qtyExpr || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qtyExpr', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="e.g. 5x3x3, 4x5, 50"
                  />
                  {row.qtyDisplay && (
                    <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                  )}
                </div>
              </div>
              
              {/* Quantity and Rate Row */}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    value={row.qty || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qty', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                    placeholder="0"
                    min="0"
                    step="0.01"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rate</label>
                  <input
                    type="number"
                    value={row.rate || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'rate', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              {/* Discount Row */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Discount</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    value={row.lineDiscountValue || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountValue', e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded text-sm"
                    placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                  />
                  <select
                    value={row.lineDiscountType || 'amount'}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountType', e.target.value)}
                    className="border border-gray-300 rounded text-sm p-2 w-20 relative z-10 appearance-none cursor-pointer bg-white text-center"
                    style={{
                      backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 6px center',
                      backgroundSize: '14px',
                      paddingRight: '24px'
                    }}
                  >
                    <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                    <option value="percent">%</option>
                  </select>
                </div>
              </div>
              
              {/* Amount Row */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-sm font-semibold text-blue-600">
                  {formatCurrency(parseFloat(row.amount) || 0, companyCurrency)}
                </div>
              </div>
              
              {/* GST Row (if applicable) */}
              {getPartyGstType(party) === 'Regular' && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">SGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.sgst) || 0, companyCurrency)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">CGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.cgst) || 0, companyCurrency)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">IGST</label>
                    <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                      {formatCurrency(parseFloat(row.igst) || 0, companyCurrency)}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Total Row */}
              <div className="bg-blue-50 p-3 rounded border">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Total:</span>
                  <span className="text-lg font-bold text-blue-600">
                    {formatCurrency(parseFloat(row.total) || parseFloat(row.amount) || 0, companyCurrency)}
                  </span>
                </div>
              </div>
            </div>
              );
            })()}

            {/* Item Numbers Pagination */}
            <div className="mt-4 flex justify-center">
              <div className="flex gap-2">
                {rows.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToItem(index)}
                    className={`w-8 h-8 rounded-full text-sm font-medium ${
                      index === currentMobileItemIndex
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6">
          <div className="flex flex-col md:flex-row gap-2 mb-4 md:mb-0">
            <button onClick={addRow} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md">Add Item Row</button>
            <button onClick={() => setShowAddItemModal(true)} className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md">Add New Item</button>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 w-full md:w-80">
            <div className="flex justify-between mb-1"><span>Subtotal (Excl. GST):</span><span>{formatCurrency(subtotal, companyCurrency)}</span></div>
            {getPartyGstType(party) === 'Regular' && (
              <>
                <div className="flex justify-between mb-1"><span>Total SGST:</span><span>{formatCurrency(totalSGST, companyCurrency)}</span></div>
                <div className="flex justify-between mb-1"><span>Total CGST:</span><span>{formatCurrency(totalCGST, companyCurrency)}</span></div>
                <div className="flex justify-between mb-1"><span>Total IGST:</span><span>{formatCurrency(totalIGST, companyCurrency)}</span></div>
              </>
            )}
            <div className="flex justify-between mt-2 font-bold text-lg">
              <span>Grand Total {getPartyGstType(party) === 'Regular' ? '(Incl. GST)' : '(No GST)'}:</span>
              <span className="text-blue-700">{formatCurrency(grandTotal, companyCurrency)}</span>
            </div>
          </div>
        </div>

        {/* Add Note and E-way Bill Buttons */}
        <div className="mb-6">
          <div className="flex gap-3 flex-wrap">
            {/* Add Note Button */}
            <button
              type="button"
              onClick={() => setShowNotesModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Note
            </button>
            
            {/* Add E-way Bill Button */}
            <button
              type="button"
              onClick={() => setShowEwayBillModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 border border-green-200 rounded-md transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add E-way Bill
            </button>
          </div>
        </div>

                <div className="flex gap-4 mt-4">
                                            <button id="add-purchase-button"
            onClick={handleSaveBill}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                    >
            {editingBillId ? 'Update Bill' : 'Save Complete Bill'}
                    </button>
                    
                    {/* Clear Form Button - Always visible */}
                    <button
                        onClick={clearBillForm}
                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                    >
                        Clear Form
                    </button>
                    
          {editingBillId && (
                        <button
              onClick={() => {
                setEditingBillId(null);
                setBillNumber('');
                setBillDate(new Date().toISOString().split('T')[0]);
                setParty('');
                setNotes('');
                setRows([{ ...initialItemRow }]);
                setCustomFields({ ewayBillNo: '', ewayQr: '', ewayDate: '' });
                setItemSearchTextByRow({});
                setCurrentMobileItemIndex(0);
              }}
                            className="flex-1 bg-gray-400 hover:bg-gray-500 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                        >
                            Cancel Edit
                        </button>
                    )}
          {editingBillId && (
                        <button
              onClick={() => handleAddPayment(bills.find(b => b.id === editingBillId))}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md shadow-md transition duration-300 ease-in-out transform hover:scale-105"
                        >
                            Add Payment
                        </button>
                    )}
                </div>

                {/* Success Modal - Mobile View (Inline after buttons) */}
                {showSuccessModal && (
                  <>
                    {/* Mobile View - Inline */}
                    <div className="mt-6 w-full mx-auto lg:hidden">
                      <div ref={successModalRef} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md w-full mx-auto relative mx-4">
                        <div className="absolute top-2 right-2 flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Press ESC to close</span>
                          <button
                            onClick={() => setShowSuccessModal(false)}
                            className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                          </h3>
                          <p className="text-gray-600">What would you like to do next?</p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={() => {
                              handleSuccessModalPrint();
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                            </svg>
                            Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowSuccessModal(false);
                              resetFormToInitialState();
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleAddPayment(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Add Payment
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleEditBill(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                          >
                            Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Desktop View - Overlay */}
                    <div className="hidden lg:fixed lg:inset-0 lg:flex lg:items-center lg:justify-center lg:z-50">
                      <div ref={successModalRef} className="bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-6 max-w-md w-full mx-auto relative">
                        <div className="absolute top-2 right-2 flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Press ESC to close</span>
                          <button 
                            onClick={() => setShowSuccessModal(false)} 
                            className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                          >
                            ×
                          </button>
                        </div>
                        
                        <div className="text-center mb-4">
                          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                          </h3>
                          <p className="text-gray-600">What would you like to do next?</p>
                        </div>
                        
                        <div className="flex flex-col gap-3">
                          <button
                            onClick={() => {
                              handleSuccessModalPrint();
                            }}
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                            </svg>
                            Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              setShowSuccessModal(false);
                              resetFormToInitialState();
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleAddPayment(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                            </svg>
                            Add Payment
                          </button>
                          
                          <button
                            onClick={() => {
                              const bill = bills.find(b => b.id === savedBillId);
                              if (bill) {
                                handleEditBill(bill);
                              }
                              setShowSuccessModal(false);
                            }}
                            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                          >
                            Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </div> {/* Close Center Panel */}
            
            {/* Right Panel - Party-wise Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-green-50 via-green-50 to-green-100 border border-green-200 rounded-lg p-4 flex flex-col shadow-sm">
              <h3 className="text-lg font-bold text-green-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Party Items</h3>

              {!selectedPartyForQuickItems ? (
                <div className="text-center text-gray-500 text-sm py-4">
                  Select a party to see their items
                </div>
              ) : (
                <>
                  <div className="text-xs text-gray-600 mb-3 text-center">
                    {parties.find(p => p.id === selectedPartyForQuickItems)?.firmName}
                  </div>
                  <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                    {partyWiseItems.map((itemData) => {
                      const item = items.find(it => it.id === itemData.itemId);
                      if (!item) return null;
                      
                      return (
                        <button
                          key={itemData.itemId || itemData.id}
                          onClick={() => handleQuickItemClick(itemData, true)}
                          className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-md p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                          title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times with this party`}
                        >
                          <div className="font-semibold text-sm text-green-900 truncate">
                            {itemData.itemName || itemData.name || 'Unknown Item'}
                          </div>
                          <div className="text-xs text-green-700 font-medium">
                            ₹{itemData.lastRate || 0} × {itemData.lastQty || 0}
                          </div>
                          <div className="text-xs text-green-600">
                            Used {itemData.count} times
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  {partyWiseItems.length === 0 && (
                    <div className="text-center text-gray-500 text-sm py-4">
                      No items used with this party
                    </div>
                  )}
                </>
              )}
            </div>
          </div> {/* Close Quick Items Grid */}
        </div> {/* Close Quick Items Section */}
        
        {/* Quick Items Modal - Compact */}
        {showQuickItemsModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowQuickItemsModal(false);
              }
            }}
          >
            <div 
              className="absolute bg-white rounded-lg shadow-xl max-w-2xl w-[calc(100vw-2rem)] max-h-[85vh] overflow-hidden"
              style={quickItemModalPosition}
            >
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Quick Items</h3>
                <button
                  onClick={() => setShowQuickItemsModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 overflow-y-auto max-h-[calc(85vh-60px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Most Used Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Most Used ({mostUsedItems.length})
                    </h4>
                    <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                      gridTemplateColumns: 
                        window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                        window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                        '1fr'
                    }}>
                      {mostUsedItems.map((itemData) => {
                        const item = items.find(it => it.id === itemData.id);
                        if (!item) return null;
                        
                        return (
                          <button
                            key={itemData.itemId || itemData.id}
                            onClick={() => {
                              handleQuickItemClick(itemData);
                              setShowQuickItemsModal(false);
                            }}
                            className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                            title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                          >
                            <div className="font-medium text-xs text-blue-900 leading-tight break-words">
                              {itemData.itemName || itemData.name || 'Unknown Item'}
                            </div>
                            <div className="text-xs text-blue-700 font-medium mt-0.5">
                              {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                            </div>
                            <div className="text-xs text-blue-600">
                              {itemData.count}×
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {mostUsedItems.length === 0 && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items used yet
                      </div>
                    )}
                  </div>

                  {/* Party-wise Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Party Items ({partyWiseItems.length})
                    </h4>
                    <select
                      value={selectedPartyForQuickItems}
                      onChange={(e) => setSelectedPartyForQuickItems(e.target.value)}
                      className="w-full p-1.5 border border-gray-300 rounded text-xs mb-2"
                    >
                      <option value="">Select Party</option>
                      {Array.from(new Set(bills.map(bill => bill.party).filter(Boolean))).map((partyId, index) => {
                        const partyName = parties.find(p => p.id === partyId)?.firmName || partyId;
                        return (
                          <option key={index} value={partyId}>{partyName}</option>
                        );
                      })}
                    </select>
                    
                    {!selectedPartyForQuickItems ? (
                      <div className="text-center text-gray-500 text-xs py-3">
                        Select a party to see their items
                      </div>
                    ) : (
                      <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                        gridTemplateColumns: 
                          window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                          window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                          '1fr'
                      }}>
                        {partyWiseItems.map((itemData) => {
                          const item = items.find(it => it.id === itemData.itemId);
                          if (!item) return null;
                          
                          return (
                            <button
                              key={itemData.itemId || itemData.id}
                              onClick={() => {
                                handleQuickItemClick(itemData);
                                setShowQuickItemsModal(false);
                              }}
                              className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded p-1.5 text-left transition-all duration-200 shadow-sm hover:shadow-md"
                              title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                            >
                              <div className="font-medium text-xs text-green-900 leading-tight break-words">
                                {itemData.itemName || itemData.name || 'Unknown Item'}
                              </div>
                              <div className="text-xs text-green-700 font-medium mt-0.5">
                                {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                              </div>
                              <div className="text-xs text-green-600">
                                {itemData.count}×
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {partyWiseItems.length === 0 && selectedPartyForQuickItems && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items found for this party
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Mobile Edit Modal */}
        {showMobileEditModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800">Edit Details</h3>
                <button
                  onClick={() => setShowMobileEditModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Purchase Bill Number'}</label>
                  <input 
                    type="text" 
                    value={billNumber} 
                    onChange={e => setBillNumber(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input 
                    type="date" 
                    value={billDate} 
                    onChange={e => setBillDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('partySeller')}</label>
                  <div className="relative party-dropdown-container">
                    <input
                      type="text"
                      value={partySearchText}
                      onChange={(e) => {
                        setPartySearchText(e.target.value);
                        if (e.target.value === '') {
                          setParty('');
                          setSelectedPartyForQuickItems('');
                        }
                      }}
                      onFocus={() => setShowPartyDropdown(true)}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                      placeholder="Search or type new party name..."
                    />
                    {showPartyDropdown && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filteredParties.map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setParty(p.id);
                              setSelectedPartyForQuickItems(p.id);
                              setPartySearchText(p.firmName);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            {p.firmName}
                          </button>
                        ))}
                        {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                          <button
                            onClick={() => {
                              setNewPartyName(partySearchText);
                              setShowAddPartyModal(true);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                          >
                            + Add "{partySearchText}" as new party
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md text-sm"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Bills List Section */}
        <div className="mt-12 w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h3 id="purchases-list-title" className="text-xl font-bold text-gray-800">Recent Bills</h3>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
            >
              {showFilters ? 'Hide Table & Filters' : 'Show Table & Filters'}
            </button>
          </div>
          
          {/* Filter Section and Bill List - Both controlled by showFilters */}
          {showFilters ? (
            <>
              <CompactFilterBar
                filters={[
                  {
                    key: 'search',
                    label: 'Search Bills & Parties',
                    type: 'text',
                    value: filterSearch,
                    placeholder: 'Search by bill number or party name...',
                    fullWidth: true
                  },
                  {
                    key: 'date',
                    label: 'Date',
                    type: 'text',
                    value: filterDate,
                    placeholder: 'DD/MM/YYYY or partial'
                  },
                  {
                    key: 'amount',
                    label: 'Amount',
                    type: 'text',
                    value: filterAmount,
                    placeholder: 'Filter by amount'
                  }
                ]}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                showClearButton={true}
                inlineSearch={true}
                className="bg-gray-50"
              />

              {bills.length === 0 ? (
                <div className="text-center text-gray-500 py-8 text-lg">No bills yet. Create your first bill above!</div>
              ) : (
                <div id="purchases-list" className="overflow-x-auto rounded-lg border border-gray-200 mb-6 shadow-sm">
              {/* Desktop Table View */}
              <div className="hidden lg:block">
                {/* Header Row */}
                <div className="bg-gray-50 border border-gray-200 rounded-t-lg p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-6 gap-2 lg:gap-4 items-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <SortableHeader
                        columnKey="number"
                        label="Bill No."
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="billDate"
                        label={t('date')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="partyName"
                        label={t('party')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="amount"
                        label={t('amount')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="paid"
                        label={t('paid')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="outstanding"
                        label={t('outstanding')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center cursor-pointer hover:text-blue-600"
                        as="div"
                      />
                    </div>
                    <div className="ml-2 lg:ml-4 w-20 lg:w-24 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </div>
                  </div>
                </div>
                
                {/* Dropdown Form Rows */}
                <div className="max-h-96 overflow-y-auto custom-scrollbar">
                  {pagination.currentData.map((bill, idx) => (
                    <DropdownFormRow
                      key={idx}
                      bill={bill}
                      docType={docType}
                      docTypeOptions={docTypeOptions}
                      companyCurrency={companyCurrency}
                      onView={handleViewBill}
                      onEdit={handleEditBill}
                      onDelete={handleDeleteBill}
                      onPrint={() => {
                                const enhancedBill = {
                                  ...bill,
                                  rows: (bill.rows || []).map(row => {
                                    if (row.item) {
                                      const itemObj = items.find(it => it.id === row.item);
                                      if (itemObj) {
                                        return {
                                          ...row,
                                          description: itemObj.itemName || row.description || '',
                                          unit: itemObj.quantityMeasurement || row.unit || '',
                                          quantityMeasurement: itemObj.quantityMeasurement || row.quantityMeasurement || ''
                                        };
                                      }
                                    }
                                    return row;
                                  }),
                                  companyDetails: company,
                                  partyDetails: parties.find(p => p.id === bill.party) || {},
                                  docType: docType
                                };
                                handleMobileInvoiceView(enhancedBill);
                      }}
                      onPayment={handleAddPayment}
                      onReceipts={handleViewPaymentReceipts}
                      receiptsCount={getBillPayments(bill)?.length || 0}
                      receiptNumbers={getBillPayments(bill)?.map(payment => payment.receiptNumber) || []}
                      items={items}
                      index={idx}
                      isExpanded={expandedRows[idx] || false}
                      onToggle={toggleRowExpansion}
                    />
                  ))}
                </div>
              </div>
              
              {/* Mobile Card View with Collapsible Dropdown */}
              <div className="lg:hidden">
                  {pagination.currentData.map((bill, idx) => (
                    <PurchaseCard 
                        key={bill.id} 
                        bill={bill}
                        docType={docType}
                        docTypeOptions={docTypeOptions}
                        companyCurrency={companyCurrency}
                        items={items}
                        index={idx}
                        isExpanded={expandedRows[idx] || false}
                        onToggle={toggleRowExpansion}
                        onView={handleViewBill}
                        onEdit={handleEditBill}
                        onDelete={handleDeleteBill}
                        onPayment={handleAddPayment}
                        onReceipts={handleViewPaymentReceipts}
                        receiptsCount={getBillPayments(bill)?.length || 0}
                        currentCompany={companyProfile}
                        receiptNumbers={getBillPayments(bill)?.map(payment => payment.receiptNumber) || []}
                        onPrint={() => {
                            // Enhance bill data with proper measurement units
                            const enhancedBill = {
                                ...bill,
                                rows: (bill.rows || []).map(row => {
                                    if (row.item) {
                                        const itemObj = items.find(it => it.id === row.item);
                                        if (itemObj) {
                                            return {
                                                ...row,
                                                description: itemObj.itemName || row.description || '',
                                                unit: itemObj.quantityMeasurement || row.unit || '',
                                                quantityMeasurement: itemObj.quantityMeasurement || row.quantityMeasurement || ''
                                            };
                                        }
                                    }
                                    return row;
                                }),
                                companyDetails: company,
                                partyDetails: parties.find(p => p.id === bill.party) || {},
                                docType: docType
                            };
                            handleMobileInvoiceView(enhancedBill);
                        }}
                    />
                  ))}
              </div>
              
              {/* Pagination Controls */}
              <div className="mt-6">
              <PaginationControls {...pagination} />
              </div>
              </div>
              )}
            </>
          ) : (
            <div className="text-center text-gray-500 py-8">
              <p className="text-lg">Table and filters are hidden</p>
              <p className="text-sm">Click "Show Table & Filters" to view purchase bills</p>
            </div>
          )}
        </div> {/* Close Bills List Section */}
                
                {/* Modals */}
                {showInvoiceModal && invoiceBill && (
                  <PreviewModal section={MODAL_SECTIONS.PURCHASES}
                    isOpen={showInvoiceModal && invoiceBill}
                    onClose={() => setShowInvoiceModal(false)}
                    title={`${docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Preview`}
                    maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-4xl"
                    maxHeight="max-h-[85vh] sm:max-h-[90vh]"
                    showPrintButton={true}
                    onPrint={handleInvoicePrint}
                    showPdfButton={true}
                    onPdf={handleInvoiceDownload}
                    extraActions={
                      <>
                        <PurchaseShareButton
                          onExportPDF={handleInvoiceSharePdf}
                          onExportImage={handleInvoiceShareImage}
                          disabled={!invoiceBill}
                        />
                      </>
                    }
                    showZoomControls={true}
                    zoom={invoiceZoom}
                    onZoomIn={() => setInvoiceZoom(z => Math.min(3, z + 0.1))}
                    onZoomOut={() => setInvoiceZoom(z => Math.max(0.5, z - 0.1))}
                    onZoomReset={() => setInvoiceZoom(1)}
                    zIndex="z-50"
                  >
                    <div 
                      className="flex-1 overflow-auto bg-gray-50 print:bg-white relative" 
                      onWheel={handleInvoiceWheel} 
                      style={{ 
                        minHeight: 0,
                        maxHeight: 'calc(100vh - 200px)', // Ensure it has a max height for scrolling
                        overflowY: 'auto'
                      }}
                    >
                      {/* Zoom instruction */}
                      <div className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md z-10 hidden sm:block">
                        Hold Ctrl + Scroll to zoom
                      </div>
                      <div className="flex justify-center items-start w-full p-4 min-h-full">
                        <div className="w-full max-w-4xl min-h-full">
                          <div 
                            ref={invoiceRef} 
                            style={{ 
                              transform: `scale(${invoiceZoom})`, 
                              transformOrigin: 'top center', 
                              transition: 'transform 0.2s', 
                              background: 'white', 
                              boxShadow: '0 0 8px #ccc', 
                              margin: '0 auto', 
                              width: '210mm', 
                              minHeight: '297mm', 
                              maxWidth: '100%', 
                              padding: 0,
                              height: 'auto' // Ensure height is auto to allow content to expand
                            }} 
                            className="print:shadow-none print:bg-white print:transform-none print:p-0 print:m-0 print:max-w-none print:w-[210mm] print:min-h-[297mm]"
                          >
                          {(() => {
                            if (docType === 'purchaseBill') {
                              // For purchase bills, select template based on party GST type
                              return getPartyGstType(invoiceBill.party) === 'Regular' ? (
                                <PurchaseBillTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              ) : (
                                <PurchaseBillNonGstTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              );
                            } else if (docType === 'purchaseOrder') {
                              return (
                                <PurchaseOrderTemplate
                                  billData={invoiceBill}
                                  companyDetails={{ ...company, gstinType: company.gstinType || '', logoUrl: company.logoUrl || '' }}
                                  partyDetails={parties.find(p => p.id === invoiceBill.party) || {}}
                                  payments={invoiceBill.payments || []}
                                />
                              );
                            } else {
                              return <div>Template not available</div>;
                            }
                          })()}
                            <style>{`
                              @media print {
                                html, body, #root, .print\\:w-[210mm] { width: 210mm !important; }
                                .print\\:min-h-[297mm] { min-height: 297mm !important; }
                                .print\\:max-w-none { max-width: none !important; }
                                .print\\:p-0 { padding: 0 !important; }
                                .print\\:m-0 { margin: 0 !important; }
                              }
                            `}</style>
                          </div>
                        </div>
                      </div>
                    </div>
                  </PreviewModal>
                )}

                {showViewModal && viewBill && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full relative">
                      <div className="absolute top-2 right-2 flex items-center space-x-2">
                        <span className="text-xs text-gray-500">Press ESC to close</span>
                        <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-800 text-xl">&times;</button>
                      </div>
                      <h3 className="text-xl font-bold mb-4 text-center">{docType === 'purchaseBill' ? 'Purchase Bill' : docType === 'purchaseOrder' ? 'Purchase Order' : 'Bill'} Summary</h3>
                      <div className="mb-2 flex flex-col gap-1">
                        <div><span className="font-semibold">Number:</span> {viewBill.number}</div>
                        <div><span className="font-semibold">Date:</span> {viewBill.billDate}</div>
                        <div><span className="font-semibold">Party:</span> {(parties.find(p => p.id === viewBill.party)?.firmName) || viewBill.party || 'Unknown'}</div>
                        <div><span className="font-semibold">GST Type:</span> {getPartyGstType(viewBill.party)}</div>
                        <div><span className="font-semibold">Amount:</span> ₹{viewBill.amount}</div>
                        {viewBill.notes && <div><span className="font-semibold">Notes:</span> {viewBill.notes}</div>}
                      </div>
                      <div className="mt-4">
                        <div className="font-semibold mb-1">Items:</div>
                        <ul className="list-disc list-inside text-sm">
                          {(viewBill.rows || []).map((row, i) => {
                            const itemObj = (items || []).find(it => it.id === row.item) || {};
                            const itemName = itemObj.itemName || row.item || '?';
                            return (
                              <li key={i}>{itemName} (Qty: {row.qty}, Rate: ₹{row.rate})</li>
                            );
                          })}
                        </ul>
                  </div>
                </div>
              </div>
            )}

      {/* Payment Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedBillForPayment(null);
        }}
        title={t('addPayment')}
        size="md"
        className="!w-[90vw] sm:!w-[60vw] !max-w-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleSavePayment();
        }}
        submitText={t('savePayment')}
        cancelText={t('cancel')}
        submitVariant="success"
      >
            {/* Bill Information */}
            {selectedBillForPayment && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
            <h4 className="font-semibold text-blue-800 mb-1 text-xs sm:text-sm">Bill Information</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 text-xs">
              <div><span className="font-medium">Bill:</span> {selectedBillForPayment.number}</div>
                  <div><span className="font-medium">Party:</span> {getPartyName(selectedBillForPayment.party)}</div>
              <div><span className="font-medium">Total:</span> {formatCurrency(selectedBillForPayment.amount, companyCurrency)}</div>
              <div><span className="font-medium">Outstanding:</span> {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                </div>
              </div>
            )}
            
        {/* Advance Information - Only show if advance is available */}
        {selectedBillForPayment && getPartyAdvance(selectedBillForPayment.party, payments) > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
            <h4 className="font-semibold text-green-800 mb-1 text-xs sm:text-sm">Advance Information</h4>
            <div className="text-xs space-y-1">
              <div><span className="font-medium">Available Advance:</span> {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                  <div className="text-xs text-green-600">
                Note: Excess payment will be allocated to other bills using FIFO method.
                  </div>
                </div>
              </div>
            )}
            
        <div className="space-y-2 sm:space-y-3">
          {/* Receipt Number and Date - Inline Edit */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-2">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-xs font-medium text-gray-700">Payment Details</h4>
              <button
                type="button"
                onClick={() => {
                  setReceiptNumber('');
                  setPaymentDate('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors text-xs"
                title="Clear and regenerate"
              >
                Regenerate
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Number</label>
                <input
                  type="text"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  placeholder="Auto-generated"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  required
                />
              </div>
            </div>
              </div>
              
          {/* Amount and Payment Mode in one line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  placeholder={t('enterPaymentAmount')}
                  step="0.01"
                  min="0"
                required
              />
              </div>
              
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                required
                >
                  <option value="Cash">{t('cash')}</option>
                  <option value="Cheque">{t('cheque')}</option>
                  <option value="Bank Transfer">{t('bankTransfer')}</option>
                  <option value="UPI">UPI</option>
                  <option value="Card">{t('card')}</option>
                </select>
            </div>
              </div>
              
          {/* Payment calculation info */}
          {selectedBillForPayment && paymentAmount && (
            <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 space-y-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <div>Outstanding: {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                <div>Advance: {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                <div className="font-medium text-blue-600 col-span-2 sm:col-span-1">
                  Payment needed: {formatCurrency(Math.max(0, getBillOutstanding(selectedBillForPayment) - getPartyAdvance(selectedBillForPayment.party, payments)), companyCurrency)}
                </div>
              </div>
            </div>
          )}
          
          {/* Reference and Notes in one line */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                  placeholder="Cheque number, UPI reference, etc."
                />
              </div>
              
              <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                rows="2"
                  placeholder={t('additionalNotes')}
                />
              </div>
            </div>
            </div>
      </FormModal>

      {/* Payment Details Modal */}
      {selectedBillForReceipts && (
        <StandardModal section={MODAL_SECTIONS.PURCHASES}
          isOpen={showPaymentDetailsModal}
          onClose={() => setShowPaymentDetailsModal(false)}
          title={t('paymentReceipts')}
          size="2xl"
          className="!w-[90vw] sm:!w-[60vw] !max-w-4xl"
        >
        <div className="mb-2">
          <p className="text-xs sm:text-sm text-gray-600 break-words">
            <span className="hidden sm:inline">{docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}: {selectedBillForReceipts?.number} | Party: {getPartyName(selectedBillForReceipts?.party)} | Total Amount: ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
            <span className="sm:hidden">{selectedBillForReceipts?.number} | ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
          </p>
            </div>
            
            {/* Payment Summary */}
        <div className="bg-gray-50 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
            <div className="text-center sm:text-left">
              <span className="font-medium text-gray-700">Total Bill:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold">₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
                </div>
            <div className="text-center sm:text-left">
              <span className="font-medium text-gray-700">Paid:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold text-green-600">₹{(selectedBillForReceipts?.paid || 0).toLocaleString('en-IN')}</span>
                </div>
            <div className="text-center sm:text-left">
                  <span className="font-medium text-gray-700">Outstanding:</span>
              <span className="ml-1 text-xs sm:text-sm font-semibold text-red-600">₹{(selectedBillForReceipts?.outstanding || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
            
        <div className="overflow-x-auto max-h-64">
          {/* Desktop Table View */}
          <div className="hidden sm:block">
            <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Receipt No</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mode</th>
                  <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                {(getBillPayments(selectedBillForReceipts) || []).map((payment) => {
                  const allocation = payment.allocations?.find(a => a.billId === selectedBillForReceipts?.id && a.billType === 'purchase');
                    return (
                      <tr key={payment.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                          {payment.receiptNumber}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          ₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {payment.paymentDate}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                          {payment.paymentMode}
                        </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        <StandardButton
                          variant="info"
                          size="sm"
                              onClick={() => {
                                setSelectedPaymentForReceipt(payment);
                                setShowReceiptModal(true);
                                setShowPaymentDetailsModal(false);
                              }}
                            >
                              Preview
                        </StandardButton>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          
          {/* Mobile Card View */}
          <div className="sm:hidden space-y-2">
            {(getBillPayments(selectedBillForReceipts) || []).map((payment) => {
              const allocation = payment.allocations?.find(a => a.billId === selectedBillForReceipts?.id && a.billType === 'purchase');
              return (
                <div key={payment.id} className="bg-white p-2 rounded border border-gray-200">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">{payment.receiptNumber}</div>
                      <div className="text-xs text-gray-500">₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}</div>
                      <div className="text-xs text-gray-500">{payment.paymentDate} • {payment.paymentMode}</div>
          </div>
                    <StandardButton
                      variant="info"
                      size="sm"
                      onClick={() => {
                        setSelectedPaymentForReceipt(payment);
                        setShowReceiptModal(true);
                        setShowPaymentDetailsModal(false);
                      }}
                    >
                      Preview
                    </StandardButton>
        </div>
                </div>
              );
            })}
          </div>
        </div>
        </StandardModal>
      )}

      {/* Payment Receipt Preview Modal */}
      <PreviewModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showReceiptModal && selectedPaymentForReceipt}
        onClose={() => setShowReceiptModal(false)}
        title={t('paymentReceiptPreview')}
        maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-2xl"
        maxHeight="max-h-[70vh] sm:max-h-[80vh]"
        extraActions={
          <ShareButton
            onExportPDF={async () => {
              try {
                const jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                if (!jsPDFLocal || !selectedPaymentForReceipt) return;
                const doc = new jsPDFLocal();
                let y = 15;
                doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                doc.text(`Receipt No: ${selectedPaymentForReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                addKV('Date', selectedPaymentForReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                addKV('Received From', selectedPaymentForReceipt.partyName || '');
                addKV('Amount', `₹${parseFloat(selectedPaymentForReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                addKV('Payment Mode', selectedPaymentForReceipt.paymentMode || 'Cash');
                addKV('Reference', selectedPaymentForReceipt.reference || '-');
                if (selectedPaymentForReceipt.notes) addKV('Notes', selectedPaymentForReceipt.notes);
                const blob = doc.output('blob');
                const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`, { type: 'application/pdf' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                } else {
                  doc.save(`receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`);
                }
              } catch (e) { console.error('Share receipt PDF failed:', e); }
            }}
            onExportImage={async () => {
              try {
                const el = document.querySelector('.receipt-container');
                if (!el || !selectedPaymentForReceipt) return;
                const html2canvas = await import('html2canvas');
                const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.png`, { type: 'image/png' });
                if (navigator.canShare && navigator.canShare({ files: [file] })) {
                  await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                } else {
                  const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedPaymentForReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                }
              } catch (e) { console.error('Share receipt image failed:', e); }
            }}
          />
        }
        showBackButton={true}
        onBack={() => {
          setShowReceiptModal(false);
          setShowPaymentDetailsModal(true);
        }}
        showPrintButton={true}
        onPrint={printReceipt}
        showPdfButton={true}
        onPdf={generatePDFReceipt}
        zIndex="z-50"
      >
        <div ref={receiptRef} className="flex justify-center">
          <ReceiptTemplate
            receipt={{
              totalAmount: selectedPaymentForReceipt?.totalAmount,
              date: selectedPaymentForReceipt?.paymentDate,
              mode: selectedPaymentForReceipt?.paymentMode,
              reference: selectedPaymentForReceipt?.reference,
              notes: selectedPaymentForReceipt?.notes,
              billReference: selectedPaymentForReceipt?.billId,
              allocations: selectedPaymentForReceipt?.allocations,
              advanceAllocations: selectedPaymentForReceipt?.advanceAllocations,
              fifoAllocationUsed: selectedPaymentForReceipt?.fifoAllocationUsed,
              remainingAmount: selectedPaymentForReceipt?.remainingAmount
            }}
            bill={{
              number: selectedPaymentForReceipt?.billNumber,
              invoiceNumber: selectedPaymentForReceipt?.billNumber,
              billNumber: selectedPaymentForReceipt?.billNumber
            }}
            company={company}
            party={{ firmName: selectedPaymentForReceipt?.partyName }}
            receiptNumber={selectedPaymentForReceipt?.receiptNumber}
            fifoAllocation={selectedPaymentForReceipt?.allocations}
            isPurchase={true}
          />
        </div>
      </PreviewModal>


      {/* ADD ITEM Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showAddItemModal}
        onClose={() => { setShowAddItemModal(false); clearAddItemForm(); }}
        title={t('addNewItem')}
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddItem();
        }}
        submitText={t('addItem')}
        cancelText={t('cancel')}
        submitVariant="success"
      >
        {addItemMessage && (
          <div className={`mb-4 p-3 rounded ${addItemMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addItemMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('itemName')} *</label>
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('enterItemName')}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('quantityMeasurement')}</label>
            <input
              type="text"
              value={newItemMeasurement}
              onChange={(e) => setNewItemMeasurement(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., kg, pcs, liters"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('salePricePerUnit')}</label>
            <input
              type="number"
              value={newItemRate}
              onChange={(e) => setNewItemRate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('purchasePricePerUnit')}</label>
            <input
              type="number"
              value={newItemPurchasePrice}
              onChange={(e) => setNewItemPurchasePrice(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
            <input
              type="text"
              value={newItemHsnCode}
              onChange={(e) => setNewItemHsnCode(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., 48025510"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
            <input
              type="number"
              value={newItemGstPercentage}
              onChange={(e) => setNewItemGstPercentage(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
              min="0"
              max="100"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Opening Stock</label>
            <input
              type="number"
              value={newItemCurrentStock}
              onChange={(e) => setNewItemCurrentStock(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="0"
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custom Unit</label>
            <input
              type="text"
              value={newItemCustomUnit}
              onChange={(e) => setNewItemCustomUnit(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., box, set, pair"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('itemDescription')}</label>
          <textarea
            value={newItemDescription}
            onChange={(e) => setNewItemDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder={t('enterItemDescription')}
          />
        </div>
      </FormModal>

      {/* ADD PARTY Modal */}
      <FormModal section={MODAL_SECTIONS.PURCHASES}
        isOpen={showAddPartyModal}
        onClose={() => { setShowAddPartyModal(false); clearAddPartyForm(); }}
        title="Add New Party"
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddParty();
        }}
        submitText="Add Party"
        cancelText="Cancel"
        submitVariant="success"
      >
        {addPartyMessage && (
          <div className={`mb-4 p-3 rounded ${addPartyMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addPartyMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Party Name *</label>
            <input
              type="text"
              value={newPartyName}
              onChange={(e) => setNewPartyName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter party name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST Type</label>
            <select
              value={newPartyGstType}
              onChange={(e) => setNewPartyGstType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Regular">Regular</option>
              <option value="Composition">Composition</option>
              <option value="Unregistered">Unregistered</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GSTIN {newPartyGstType === 'Regular' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newPartyGstin}
              onChange={(e) => setNewPartyGstin(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={newPartyGstType === 'Regular' ? 'Enter GSTIN (required for Regular)' : 'Enter GSTIN (if applicable)'}
              required={newPartyGstType === 'Regular'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={newPartyPhone}
              onChange={(e) => setNewPartyPhone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newPartyEmail}
              onChange={(e) => setNewPartyEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={newPartyAddress}
            onChange={(e) => setNewPartyAddress(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder="Enter party address"
          />
        </div>
        
        {/* Advanced Fields Toggle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowAdvancedFields(!showAdvancedFields)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            <svg className={`w-4 h-4 transition-transform ${showAdvancedFields ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showAdvancedFields ? 'Hide' : 'Show'} Advanced Fields (Opening Balance, Credit Limit)
          </button>
        </div>
        
        {/* Advanced Fields */}
        {showAdvancedFields && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Advanced Settings (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                <input
                  type="number"
                  value={newPartyOpeningBalance}
                  onChange={(e) => setNewPartyOpeningBalance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">+ve: Supplier owes you, -ve: You owe supplier</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (₹)</label>
                <input
                  type="number"
                  value={newPartyCreditLimit}
                  onChange={(e) => setNewPartyCreditLimit(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum credit allowed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Period (Days)</label>
                <input
                  type="number"
                  value={newPartyCreditPeriod}
                  onChange={(e) => setNewPartyCreditPeriod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Payment terms in days</p>
              </div>
            </div>
          </div>
        )}
      </FormModal>

      {/* Notes Modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add Note</h3>
              <button
                onClick={() => setShowNotesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                className="w-full h-64 border border-gray-300 rounded-md shadow-sm p-3 resize-none"
                placeholder="Enter additional notes..."
                autoFocus
              />
              <div className="mt-2 text-right text-sm text-gray-500">
                {notes.length} characters
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* E-way Bill Modal */}
      {showEwayBillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Add E-way Bill Details</h3>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill No</label>
                  <input
                    type="text"
                    value={customFields.ewayBillNo}
                    onChange={e => setCustomFields(f => ({ ...f, ewayBillNo: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter E-way Bill number"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-way Bill Date</label>
                  <input
                    type="date"
                    value={customFields.ewayDate}
                    onChange={e => setCustomFields(f => ({ ...f, ewayDate: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">QR Code URL</label>
                  <input
                    type="text"
                    value={customFields.ewayQr}
                    onChange={e => setCustomFields(f => ({ ...f, ewayQr: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-3"
                    placeholder="Enter QR code URL"
                  />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t">
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowEwayBillModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
              >
                Save E-way Bill
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    );
}

export default PurchasesDev; 